import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  closestCorners,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { renderField } from '../../components/generic/fieldRenderers';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { Button, Label, Switch, TextInput, Textarea, Select, Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui';
import Badge from '../../components/ui/Badge';
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  CogIcon,
  CopyIcon,
  DocumentTextIcon,
  DownloadIcon,
  ExpandAllIcon,
  CollapseAllIcon,
  FitWidthIcon,
  EyeIcon,
  GripVerticalIcon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  SaveIcon,
  SearchIcon,
  TableIcon,
  TrashIcon,
  UndoIcon,
  UnlockIcon,
  UploadCloudIcon,
  CameraIcon,
  ViewColumnsIcon,
  XIcon,
} from '../../components/ui/Icons';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { formatFileSize } from '../../components/attachments/fileIconUtils';
import {
  downloadEFormSourceFile,
  publishEFormDefinition,
  restoreEFormVersion,
  saveEFormDraftSchema,
  unpublishEFormDefinition,
  updateEFormDefinition,
} from '../../services/mutations';
import {
  EFormFieldSchema,
  EFormFieldType,
  EFormFieldVisibleWhen,
  EFormListColumnConfig,
  EFormOptionsSource,
  EFormSchema,
  EFormSectionColumns,
  EFormSectionSchema,
  EFormSubformNestedType,
  EFormVisibilityOperator,
  EFORM_COL_SPANS,
  EFORM_SECTION_COLUMNS,
  EFORM_SUBFORM_NESTED_TYPES,
  FIELD_PALETTE,
  compileFormSchema,
  createPaletteField,
  createSubformNestedField,
  defaultListColumns,
  duplicateField,
  duplicateSection,
  eformDisplayPreviewText,
  eformSpacerHeight,
  EFORM_SPACER_PRESETS,
  eligibleFilterFields,
  emptyEFormSchema,
  flattenSchemaFields,
  isEFormDisplayField,
  isEFormSchema,
  listColumnLabel,
  newSectionId,
  colSpanOptionLabel,
  defaultColSpanForSection,
  fieldColSpanClass,
  normalizeFieldColSpan,
  normalizeSectionColumns,
  resolveFieldColSpan,
  sectionTwelveGridClass,
  orderedFilterFieldsForBuilder,
  resolveFilterFields,
  resolveListColumns,
  validateSchemaForPublish,
} from './schema';
import { EFormRuntime } from './EFormRuntime';
import ImportFromFileModal from './ImportFromFileModal';
import { EFORM_REFERENCE_SOURCES } from './referenceSources';
import { useSchemaHistory, isEditableKeyboardTarget } from './useSchemaHistory';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';
import {
  buildEFormPackage,
  downloadJsonFile,
  parseEFormImportJson,
  safeDownloadBasename,
} from './formPackage';
import {
  type DropIndicator,
  moveFieldInSchema,
  nudgeFieldsInSchema,
  rangeSelectFieldIds,
  nextZoom,
  CANVAS_ZOOM_STEPS,
} from './builderFieldOps';
import { diffEFormSchemas, type EFormSchemaDiff } from './schemaDiff';
import {
  useApprovalTemplates,
  useApprovalTypes,
} from '../approvals/useData';
import {
  buildSidebarPlacements,
  normalizeSidebarPlacements,
  type EFormSidebarPlacement,
} from './sidebarPlacements';
import { collectSidebarPathLabels, getSidebarItems } from '../../components/Layout/config/SidebarItems';
import type { MenuItem as SidebarMenuItem } from '../../components/Layout/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { useModuleEntitlements } from '../../modules/admin/module-management';
import {
  EFormDefinition,
  EFormVersion,
  getDraftVersion,
  getPublishedVersion,
  useEFormDefinition,
} from './useData';
import {
  ReferenceSidebarCollapseButton,
  ReferenceSidebarSplitter,
} from '../parameters/reference-workspaces/ReferenceSidebarChrome';
import { useResizablePanel } from '../parameters/reference-workspaces/useResizablePanel';
import { useFormStudioActions } from './FormStudioContext';

const VISIBILITY_OPERATORS: Array<{ value: EFormVisibilityOperator; label: string }> = [
  { value: 'eq', label: 'Equals' },
  { value: 'neq', label: 'Not equals' },
  { value: 'empty', label: 'Is empty' },
  { value: 'notEmpty', label: 'Is not empty' },
  { value: 'contains', label: 'Contains' },
];

const FIELD_TYPE_BADGE: Record<EFormFieldType, string> = {
  text: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  textarea: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  email: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  phone: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  number: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  date: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  time: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  select: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  radio: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  checkbox: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  switch: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  checklist: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  richtext: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200',
  signature: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  heading: 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-200',
  spacer: 'bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300',
  file: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  photo: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  subform: 'bg-cyan-100 text-cyan-900 dark:bg-cyan-900/40 dark:text-cyan-200',
};

function fieldTypeShortLabel(field: EFormFieldSchema): string {
  if (isEFormDisplayField(field)) {
    if (field.type === 'heading') return 'heading';
    if (field.type === 'spacer') return 'space';
    if (field.type === 'textarea') return 'memo';
    if (field.type === 'richtext') return 'formatted';
    return 'text';
  }
  return field.type;
}

function fieldCardHint(field: EFormFieldSchema, colSpan: number): string {
  const parts = [
    isEFormDisplayField(field) ? eformDisplayPreviewText(field) : field.label || 'Untitled',
    fieldTypeShortLabel(field),
    `${colSpan}/12`,
  ];
  if (isEFormDisplayField(field)) parts.push('layout');
  if (field.optionsSource === 'reference') parts.push('reference');
  if (field.required) parts.push('required');
  if (field.readOnly && !isEFormDisplayField(field)) parts.push('read-only');
  if (field.type === 'subform') {
    const n = (field.fields ?? []).length;
    parts.push(`${n} column${n === 1 ? '' : 's'}`);
    parts.push(field.rowEditor === 'inline' ? 'inline' : 'modal');
  }
  return parts.filter(Boolean).join(' · ');
}

function FieldTypeIcon({ type, className = 'w-3.5 h-3.5' }: { type: EFormFieldType; className?: string }) {
  switch (type) {
    case 'date':
      return <CalendarIcon className={className} />;
    case 'time':
      return <ClockIcon className={className} />;
    case 'file':
      return <UploadCloudIcon className={className} />;
    case 'photo':
      return <CameraIcon className={className} />;
    case 'subform':
      return <TableIcon className={className} />;
    case 'checklist':
    case 'checkbox':
    case 'switch':
      return <CheckIcon className={className} />;
    case 'select':
    case 'radio':
      return <ViewColumnsIcon className={className} />;
    case 'heading':
      return <DocumentTextIcon className={className} />;
    case 'spacer':
      return <ExpandAllIcon className={className} />;
    default:
      return <DocumentTextIcon className={className} />;
  }
}

function PanelHeading({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}) {
  const { colors } = useTheme();
  return (
    <div className="mb-3">
      <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      {hint ? <p className={`text-xs mt-0.5 ${colors.textMuted}`}>{hint}</p> : null}
    </div>
  );
}

function VisibleWhenEditor({
  value,
  fieldOptions,
  onChange,
}: {
  value?: EFormFieldVisibleWhen;
  fieldOptions: Array<{ value: string; label: string }>;
  onChange: (next: EFormFieldVisibleWhen | undefined) => void;
}) {
  const rule = value ?? { fieldKey: '', operator: 'eq' as EFormVisibilityOperator, value: '' };
  const needsValue = rule.operator === 'eq' || rule.operator === 'neq' || rule.operator === 'contains';

  return (
    <div className="space-y-2 rounded border border-dashed p-2">
      <div className="flex items-center justify-between gap-2">
        <Label>Visible when</Label>
        {value ? (
          <button
            type="button"
            className="text-xs text-red-600 hover:underline"
            onClick={() => onChange(undefined)}
          >
            Clear
          </button>
        ) : null}
      </div>
      <div>
        <Label>Field</Label>
        <Select
          value={rule.fieldKey}
          onChange={(e) => {
            const fieldKey = e.target.value;
            if (!fieldKey) {
              onChange(undefined);
              return;
            }
            onChange({ ...rule, fieldKey });
          }}
          options={[{ value: '', label: '— Always visible —' }, ...fieldOptions]}
        />
      </div>
      <div>
        <Label>Operator</Label>
        <Select
          value={rule.operator}
          onChange={(e) =>
            onChange({
              ...rule,
              fieldKey: rule.fieldKey || fieldOptions[0]?.value || '',
              operator: e.target.value as EFormVisibilityOperator,
            })
          }
          options={VISIBILITY_OPERATORS}
          disabled={!rule.fieldKey}
        />
      </div>
      {needsValue ? (
        <div>
          <Label>Value</Label>
          <TextInput
            value={rule.value ?? ''}
            disabled={!rule.fieldKey}
            onChange={(e) =>
              onChange({
                ...rule,
                fieldKey: rule.fieldKey || fieldOptions[0]?.value || '',
                value: e.target.value,
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function SortableFieldCard({
  field,
  sectionId,
  selected,
  multiSelected,
  dropEdge,
  onSelect,
  onDelete,
  onDuplicate,
}: {
  field: EFormFieldSchema;
  sectionId: string;
  selected: boolean;
  multiSelected?: boolean;
  dropEdge?: 'before' | 'after' | null;
  onSelect: (e: React.MouseEvent) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    data: { type: 'field', field, sectionId },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.35 : undefined,
  };
  const { colors } = useTheme();
  const colSpan = resolveFieldColSpan(field);
  const compact = colSpan <= 4;
  const title = isEFormDisplayField(field)
    ? eformDisplayPreviewText(field)
    : field.label || 'Untitled';
  const hint = fieldCardHint(field, colSpan);

  return (
    <div
      ref={setNodeRef}
      style={style}
      title={hint}
      className={cn(
        'group relative rounded-xl border cursor-pointer self-start transition-shadow',
        compact ? 'px-2 py-1.5' : 'px-3 py-2.5',
        colors.border,
        colors.surface,
        'hover:shadow-sm',
        selected && !isDragging && 'ring-2 ring-blue-500/70 border-blue-300 dark:border-blue-700 shadow-sm',
        multiSelected && !selected && 'ring-2 ring-blue-400/40 border-blue-200 dark:border-blue-800',
        isDragging && 'z-10 shadow-md ring-2 ring-blue-400/40',
        fieldColSpanClass(colSpan),
      )}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e);
      }}
    >
      {dropEdge === 'before' ? (
        <div
          className="pointer-events-none absolute left-1 right-1 -top-1 z-20 h-0.5 rounded-full bg-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.25)]"
          aria-hidden
        />
      ) : null}
      {dropEdge === 'after' ? (
        <div
          className="pointer-events-none absolute left-1 right-1 -bottom-1 z-20 h-0.5 rounded-full bg-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.25)]"
          aria-hidden
        />
      ) : null}
      <div className={cn('flex items-center', compact ? 'gap-1' : 'gap-2')}>
        <button
          type="button"
          className={cn(
            'cursor-grab rounded-md transition-colors shrink-0',
            compact ? 'p-0.5' : 'p-1',
            colors.textMuted,
            'hover:bg-slate-100 dark:hover:bg-slate-800',
          )}
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          aria-label="Drag field"
        >
          <GripVerticalIcon className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        </button>
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg',
            compact ? 'h-6 w-6' : 'h-8 w-8',
            FIELD_TYPE_BADGE[field.type],
          )}
        >
          <FieldTypeIcon type={field.type} className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn('font-medium truncate', compact ? 'text-xs' : 'text-sm')}>
            {title}
          </div>
          {compact ? null : (
            <div className={cn('mt-0.5 truncate text-[11px]', colors.textMuted)}>
              <span
                className={cn(
                  'mr-1 inline-flex items-center rounded-md px-1.5 py-0.5 font-medium capitalize',
                  FIELD_TYPE_BADGE[field.type],
                )}
              >
                {fieldTypeShortLabel(field)}
              </span>
              <span>· {colSpan}/12</span>
              {isEFormDisplayField(field) ? <span> · layout</span> : null}
              {field.optionsSource === 'reference' ? <span> · reference</span> : null}
              {field.required ? (
                <span className="text-amber-700 dark:text-amber-300"> · required</span>
              ) : null}
              {field.readOnly && !isEFormDisplayField(field) ? <span> · read-only</span> : null}
              {field.type === 'subform' ? (
                <span>
                  {' '}
                  · {(field.fields ?? []).length} column
                  {(field.fields ?? []).length === 1 ? '' : 's'} ·{' '}
                  {field.rowEditor === 'inline' ? 'inline' : 'modal'}
                </span>
              ) : null}
            </div>
          )}
          {!compact && field.type === 'signature' ? (
            <div
              className={cn(
                'mt-2 rounded-lg border border-dashed px-2 py-4 text-center text-[11px]',
                colors.border,
                colors.textMuted,
              )}
            >
              Signature pad · draw or upload
              {field.signatureWithStamp ? ' · stamp behind' : ''}
            </div>
          ) : null}
          {!compact && field.type === 'photo' ? (
            <div className="mt-2 flex items-center gap-2">
              <div
                className={cn(
                  'flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900',
                  colors.border,
                )}
              >
                <span className={cn('text-[10px] leading-tight', colors.textMuted)}>—</span>
              </div>
              <span className={cn('text-[11px]', colors.textMuted)}>Round photo · upload one image</span>
            </div>
          ) : null}
          {!compact && field.type === 'subform' && (field.fields ?? []).length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {(field.fields ?? []).map((nf) => (
                <span
                  key={nf.id}
                  className={cn(
                    'rounded border px-1.5 py-0.5 text-[10px]',
                    colors.border,
                    colors.textMuted,
                  )}
                >
                  {nf.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <div
          className={cn(
            'flex items-center',
            compact
              ? cn(
                  'absolute -right-1 -top-2 z-20 gap-0 rounded-md border px-0.5 py-0.5 shadow-sm',
                  colors.border,
                  colors.surface,
                  'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
                )
              : cn(
                  'shrink-0 gap-0.5 opacity-0 pointer-events-none transition-opacity',
                  'group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto',
                ),
          )}
        >
          <button
            type="button"
            className={cn(
              'rounded-md transition-colors hover:bg-slate-100 dark:hover:bg-slate-800',
              colors.textMuted,
              compact ? 'p-0.5' : 'p-1.5',
            )}
            title="Duplicate field"
            aria-label="Duplicate field"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <CopyIcon className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
          </button>
          <button
            type="button"
            className={cn(
              'rounded-md text-red-600 hover:text-red-900 hover:bg-red-50 transition-colors dark:hover:bg-red-950/40',
              compact ? 'p-0.5' : 'p-1.5',
            )}
            title="Remove field"
            aria-label="Remove field"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <TrashIcon className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionDropZone({
  section,
  selectedFieldId,
  selectedFieldIds,
  selectedSectionId,
  dropIndicator,
  collapsed,
  onToggleCollapse,
  dragHandleProps,
  setSectionRef,
  sectionStyle,
  isSectionDragging,
  onSelectSection,
  onSelectField,
  onDeleteField,
  onDuplicateField,
  onDeleteSection,
  onDuplicateSection,
}: {
  section: EFormSectionSchema;
  selectedFieldId: string | null;
  selectedFieldIds: string[];
  selectedSectionId: string | null;
  dropIndicator: DropIndicator | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
  dragHandleProps: Record<string, unknown>;
  setSectionRef: (node: HTMLElement | null) => void;
  sectionStyle?: React.CSSProperties;
  isSectionDragging?: boolean;
  onSelectSection: () => void;
  onSelectField: (id: string, e: React.MouseEvent) => void;
  onDeleteField: (id: string) => void;
  onDuplicateField: (id: string) => void;
  onDeleteSection: () => void;
  onDuplicateSection: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `section-drop-${section.id}`,
    data: { type: 'section', sectionId: section.id },
  });
  const { colors } = useTheme();
  const columns = normalizeSectionColumns(section.columns);
  const gridClass = cn(sectionTwelveGridClass(), 'items-start');
  const showSectionDropLine =
    dropIndicator?.kind === 'section' && dropIndicator.sectionId === section.id;

  return (
    <div
      ref={setSectionRef}
      style={sectionStyle}
      className={cn(
        'rounded-2xl border mb-4 overflow-hidden transition-shadow',
        colors.border,
        colors.surface,
        'shadow-sm',
        selectedSectionId === section.id && !selectedFieldId && 'ring-2 ring-blue-400/80',
        isOver && 'ring-2 ring-emerald-400/50',
        isSectionDragging && 'opacity-50',
      )}
      onClick={onSelectSection}
    >
      <div
        className={cn(
          'flex items-start justify-between gap-2 px-3 py-2.5 border-b',
          colors.border,
          'bg-slate-50/80 dark:bg-slate-900/40',
        )}
      >
        <div className="flex items-start gap-2 min-w-0">
          <button
            type="button"
            className={cn(
              'cursor-grab rounded-md p-1 mt-0.5 transition-colors',
              colors.textMuted,
              'hover:bg-slate-200/70 dark:hover:bg-slate-800',
            )}
            {...dragHandleProps}
            onClick={(e) => e.stopPropagation()}
            title="Drag to reorder section"
            aria-label="Drag to reorder section"
          >
            <GripVerticalIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            className={cn(
              'rounded-md p-1 mt-0.5 transition-colors',
              colors.textMuted,
              'hover:bg-slate-200/70 dark:hover:bg-slate-800',
            )}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse();
            }}
            title={collapsed ? 'Expand section' : 'Collapse section'}
            aria-label={collapsed ? 'Expand section' : 'Collapse section'}
          >
            {collapsed ? (
              <ChevronRightIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm tracking-tight">
              {section.heading || 'Untitled section'}
            </h3>
            {section.subHeading && !collapsed ? (
              <p className={`text-xs mt-0.5 ${colors.textMuted}`}>{section.subHeading}</p>
            ) : null}
            <div className={`flex flex-wrap gap-1.5 mt-1.5 text-[11px] ${colors.textMuted}`}>
              <span className="inline-flex items-center rounded-md bg-white dark:bg-slate-800 border px-1.5 py-0.5 font-medium">
                {columns} column{columns === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center rounded-md bg-white dark:bg-slate-800 border px-1.5 py-0.5">
                {section.fields.length} field{section.fields.length === 1 ? '' : 's'}
              </span>
              {section.visibleWhen?.fieldKey ? (
                <span className="inline-flex items-center rounded-md bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 px-1.5 py-0.5 font-medium">
                  Conditional
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            className={`p-1.5 rounded-lg transition-colors hover:bg-slate-200/70 dark:hover:bg-slate-800 ${colors.textMuted}`}
            title="Duplicate section"
            aria-label="Duplicate section"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicateSection();
            }}
          >
            <CopyIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors dark:hover:bg-red-950/40"
            title="Delete section"
            aria-label="Delete section"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteSection();
            }}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
      {!collapsed ? (
        <div
          ref={setNodeRef}
          className={cn(
            'relative min-h-[88px] p-3',
            isOver && 'bg-emerald-50/40 dark:bg-emerald-950/20',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {showSectionDropLine ? (
            <div
              className="pointer-events-none absolute inset-x-3 bottom-2 z-20 h-0.5 rounded-full bg-blue-500"
              aria-hidden
            />
          ) : null}
          <SortableContext items={section.fields.map((f) => f.id)} strategy={rectSortingStrategy}>
            <div className={gridClass}>
              {section.fields.map((field) => (
                <SortableFieldCard
                  key={field.id}
                  field={field}
                  sectionId={section.id}
                  selected={selectedFieldId === field.id}
                  multiSelected={selectedFieldIds.includes(field.id)}
                  dropEdge={
                    dropIndicator?.kind === 'field' && dropIndicator.overId === field.id
                      ? dropIndicator.edge
                      : null
                  }
                  onSelect={(e) => onSelectField(field.id, e)}
                  onDelete={() => onDeleteField(field.id)}
                  onDuplicate={() => onDuplicateField(field.id)}
                />
              ))}
            </div>
          </SortableContext>
          {section.fields.length === 0 ? (
            <div
              className={cn(
                'flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center',
                colors.border,
                isOver && 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/30',
              )}
            >
              <p className={`text-sm font-medium ${colors.text}`}>Drop fields here</p>
              <p className={`text-xs mt-1 ${colors.textMuted}`}>
                Drag an element from the palette into this section
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div ref={setNodeRef} className="h-0 overflow-hidden" aria-hidden />
      )}
    </div>
  );
}

function SortableSection({
  section,
  ...rest
}: {
  section: EFormSectionSchema;
  selectedFieldId: string | null;
  selectedFieldIds: string[];
  selectedSectionId: string | null;
  dropIndicator: DropIndicator | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSelectSection: () => void;
  onSelectField: (id: string, e: React.MouseEvent) => void;
  onDeleteField: (id: string) => void;
  onDuplicateField: (id: string) => void;
  onDeleteSection: () => void;
  onDuplicateSection: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: { type: 'section-sort', sectionId: section.id },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <SectionDropZone
      section={section}
      setSectionRef={setNodeRef}
      sectionStyle={style}
      isSectionDragging={isDragging}
      dragHandleProps={{ ...attributes, ...listeners }}
      {...rest}
    />
  );
}

function FormSettingsPanel({
  schema,
  onChange,
  formName,
  isActive,
  onIsActiveChange,
  description,
  onDescriptionChange,
}: {
  schema: EFormSchema;
  onChange: (next: EFormSchema) => void;
  formName: string;
  isActive: boolean;
  onIsActiveChange: (next: boolean) => void;
  description: string;
  onDescriptionChange: (next: string) => void;
}) {
  const { colors } = useTheme();
  const settings = schema.settings ?? {};
  const normalizedPlacements = normalizeSidebarPlacements(settings.sidebarPlacements);
  const shortcutPlacements = normalizedPlacements.filter(
    (p): p is Extract<EFormSidebarPlacement, { mode: 'shortcut' }> => p.mode === 'shortcut',
  );
  const catalogOnly = normalizedPlacements.some((p) => p.mode === 'catalog_only');
  const [sidebarPlacementOpen, setSidebarPlacementOpen] = useState(false);

  const { user, can, permissionsLoaded } = useAuth();
  const { entitlements } = useModuleEntitlements();
  const vesselAccountId = useAccountVesselScope();
  const canView = useMemo(() => {
    if (!(permissionsLoaded || user?.role === 'ADMIN')) return undefined;
    return (moduleKey: string) => can(moduleKey, 'view');
  }, [can, permissionsLoaded, user?.role]);
  const pathLabels = useMemo(
    () => collectSidebarPathLabels(getSidebarItems(canView, vesselAccountId, entitlements)),
    [canView, vesselAccountId, entitlements],
  );

  const { data: approvalTypes = [] } = useApprovalTypes();
  const { data: approvalTemplates = [] } = useApprovalTemplates();
  const eFormApprovalTypeId = useMemo(
    () => approvalTypes.find((t) => t.code === 'E_FORM_SUBMISSION')?.id,
    [approvalTypes],
  );
  const eFormTemplateOptions = useMemo(() => {
    const rows =
      eFormApprovalTypeId == null
        ? []
        : approvalTemplates.filter(
            (t) => t.isActive !== false && t.approvalTypeId === eFormApprovalTypeId,
          );
    return [
      { value: '', label: 'Default template' },
      ...rows.map((t) => ({
        value: t.code,
        label: t.name || t.code,
        description: t.code,
      })),
    ];
  }, [approvalTemplates, eFormApprovalTypeId]);

  return (
    <div className="space-y-3">
      <PanelHeading title="Form settings" hint="Status, instructions, sidebar, and admin notes" />
      <div className="flex items-center gap-2">
        <Switch checked={isActive} onChange={(e) => onIsActiveChange(e.target.checked)} />
        <div>
          <span className="text-sm">Enabled</span>
          <p className={`text-xs ${colors.textMuted}`}>
            Disabled forms cannot accept new submissions.
          </p>
        </div>
      </div>
      <div>
        <Label>Instructions</Label>
        <Textarea
          rows={3}
          value={schema.instructions ?? ''}
          onChange={(e) => onChange({ ...schema, instructions: e.target.value })}
          placeholder="Shown above the fill form"
        />
      </div>
      <div className="flex items-start gap-2">
        <Switch
          checked={Boolean(settings.requireApproval)}
          onChange={(e) =>
            onChange({
              ...schema,
              settings: {
                ...settings,
                requireApproval: e.target.checked,
                ...(e.target.checked ? {} : { approvalTemplateCode: undefined }),
              },
            })
          }
        />
        <div className="flex-1 min-w-0 space-y-2">
          <div>
            <span className="text-sm">Require approval</span>
            <p className={`text-xs ${colors.textMuted}`}>
              Submitting creates a request under Approvals (E-Form submission).
            </p>
          </div>
          {settings.requireApproval ? (
            <div className="space-y-1">
              <Label>Approval template</Label>
              <Select
                name="approvalTemplateCode"
                value={settings.approvalTemplateCode ?? ''}
                onChange={(e) => {
                  const code = String(e.target.value || '').trim();
                  onChange({
                    ...schema,
                    settings: {
                      ...settings,
                      requireApproval: true,
                      approvalTemplateCode: code || undefined,
                    },
                  });
                }}
                options={eFormTemplateOptions}
                placeholder="Default template"
                showClearButton={false}
                searchPlaceholder="Search templates…"
              />
              <p className={`text-xs ${colors.textMuted}`}>
                Create additional flows under{' '}
                <Link to="/approvals/templates" className="underline">
                  Approvals → Templates
                </Link>{' '}
                (type: E-Form submission), then select one here.
              </p>
            </div>
          ) : null}
        </div>
      </div>
      <div className={cn('rounded-lg border p-2 space-y-2', colors.border)}>
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-medium">Sidebar placement</h4>
          <Button type="button" variant="secondary" size="sm" onClick={() => setSidebarPlacementOpen(true)}>
            Configure
          </Button>
        </div>
        <p className={`text-xs ${colors.textMuted}`}>
          Place a module shortcut by dragging onto the sidebar preview.
        </p>

        <div className="flex items-center gap-2">
          <Switch
            checked={catalogOnly}
            onChange={(e) =>
              onChange({
                ...schema,
                settings: {
                  ...settings,
                  sidebarPlacements: buildSidebarPlacements(shortcutPlacements, e.target.checked),
                },
              })
            }
          />
          <span className="text-xs">Also show under E-Forms</span>
        </div>

        <div className="space-y-1.5">
          {catalogOnly && !shortcutPlacements.length ? (
            <div className={cn('rounded border px-2 py-1.5 text-xs', colors.border, colors.background)}>
              Only in E-Forms catalog
            </div>
          ) : null}
          {!catalogOnly && !shortcutPlacements.length ? (
            <div className={cn('rounded border px-2 py-1.5 text-xs', colors.border, colors.background)}>
              Hidden from sidebar
            </div>
          ) : null}
          {shortcutPlacements.map((p, idx) => (
            <div
              key={`${p.anchorPath}-${p.insert}-${idx}`}
              className={cn(
                'rounded border px-2 py-1.5 text-xs flex items-start justify-between gap-2',
                colors.border,
                colors.background,
              )}
              title={p.anchorPath}
            >
              <div className="min-w-0">
                <div className="font-medium truncate">{formName || 'E-Form'}</div>
                <div className={cn('mt-0.5 truncate', colors.textMuted)}>
                  {p.insert === 'before' ? 'Before' : 'After'} ·{' '}
                  {pathLabels[p.anchorPath] ?? p.anchorPath}
                </div>
              </div>
              {catalogOnly ? (
                <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide', colors.textMuted)}>
                  +catalog
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <SidebarPlacementEditorModal
        isOpen={sidebarPlacementOpen}
        onClose={() => setSidebarPlacementOpen(false)}
        schema={schema}
        formName={formName}
        onChange={onChange}
      />

      <div>
        <PanelHeading title="Admin notes" hint="Internal description (not shown to fillers)" />
        <Textarea
          rows={3}
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="Optional notes for admins…"
        />
      </div>
    </div>
  );
}

function ListColumnsPanel({
  schema,
  onChange,
}: {
  schema: EFormSchema;
  onChange: (next: EFormSchema) => void;
}) {
  const { colors } = useTheme();
  const settings = schema.settings ?? {};
  const listColumns = resolveListColumns(schema);

  const setListColumns = (next: EFormListColumnConfig[]) => {
    onChange({
      ...schema,
      settings: { ...settings, listColumns: next },
    });
  };

  const moveColumn = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= listColumns.length) return;
    const next = [...listColumns];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    setListColumns(next);
  };

  return (
    <div className="space-y-3">
      <PanelHeading
        title="List columns"
        hint="Columns shown on the submissions list for this form"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className={`text-xs ${colors.textMuted} hover:underline`}
          onClick={() =>
            setListColumns(defaultListColumns(schema).map((c) => ({ ...c, visible: true })))
          }
        >
          Show all
        </button>
        <button
          type="button"
          className={`text-xs ${colors.textMuted} hover:underline`}
          onClick={() => setListColumns(defaultListColumns(schema))}
        >
          Reset
        </button>
      </div>
      <p className={`text-xs ${colors.textMuted}`}>
        Toggle visibility and reorder columns for the submissions table.
      </p>
      <ul className="space-y-1">
        {listColumns.map((col, index) => (
          <li
            key={col.key}
            className={cn(
              'flex items-center gap-2 rounded border px-2 py-1.5',
              colors.border,
              colors.background,
            )}
          >
            <input
              type="checkbox"
              className="rounded"
              checked={col.visible}
              onChange={(e) => {
                const next = [...listColumns];
                next[index] = { ...col, visible: e.target.checked };
                setListColumns(next);
              }}
              aria-label={`Show ${listColumnLabel(schema, col.key)}`}
            />
            <span className="flex-1 min-w-0 text-sm truncate" title={col.key}>
              {listColumnLabel(schema, col.key)}
            </span>
            <div className="flex items-center gap-0.5 shrink-0">
              <button
                type="button"
                className={cn(
                  'p-1 rounded text-xs',
                  colors.textMuted,
                  index === 0 && 'opacity-30 pointer-events-none',
                )}
                title="Move up"
                aria-label="Move up"
                onClick={() => moveColumn(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className={cn(
                  'p-1 rounded text-xs',
                  colors.textMuted,
                  index === listColumns.length - 1 && 'opacity-30 pointer-events-none',
                )}
                title="Move down"
                aria-label="Move down"
                onClick={() => moveColumn(index, 1)}
              >
                ↓
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ListFiltersPanel({
  schema,
  onChange,
}: {
  schema: EFormSchema;
  onChange: (next: EFormSchema) => void;
}) {
  const { colors } = useTheme();
  const settings = schema.settings ?? {};
  const eligible = eligibleFilterFields(schema);
  const enabledKeys = resolveFilterFields(schema);
  const enabledSet = new Set(enabledKeys);
  const displayFields = orderedFilterFieldsForBuilder(schema);

  const setFilterFields = (next: string[]) => {
    onChange({
      ...schema,
      settings: { ...settings, filterFields: next },
    });
  };

  const toggleKey = (key: string, on: boolean) => {
    if (on) {
      if (enabledSet.has(key)) return;
      // Store in form field order (submissions list uses the same order).
      const next = eligible.map((f) => f.key).filter((k) => k === key || enabledSet.has(k));
      setFilterFields(next);
      return;
    }
    setFilterFields(enabledKeys.filter((k) => k !== key));
  };

  return (
    <div className="space-y-3 mt-5 pt-4 border-t border-slate-200 dark:border-slate-700">
      <PanelHeading
        title="List filters"
        hint="Dropdowns shown as filters on the submissions list"
      />
      <p className={`text-xs ${colors.textMuted}`}>
        Enable select/radio fields (including reference lookups like vessels) to appear as filters
        above the submissions table. Order matches the fields on the form.
      </p>
      {!eligible.length ? (
        <p className={`text-xs ${colors.textMuted}`}>
          Add a Select or Radio field to the form to configure filters.
        </p>
      ) : (
        <ul className="space-y-1">
          {displayFields.map((field) => {
            const checked = enabledSet.has(field.key);
            return (
              <li
                key={field.id}
                className={cn(
                  'flex items-center gap-2 rounded border px-2 py-1.5',
                  colors.border,
                  colors.background,
                )}
              >
                <input
                  type="checkbox"
                  className="rounded"
                  checked={checked}
                  onChange={(e) => toggleKey(field.key, e.target.checked)}
                  aria-label={`Filter by ${field.label}`}
                />
                <span className="flex-1 min-w-0 text-sm truncate" title={field.key}>
                  {field.label}
                  {field.optionsSource === 'reference' && field.referenceKey ? (
                    <span className={cn('ml-1 text-xs', colors.textMuted)}>
                      · {field.referenceKey}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {enabledKeys.length ? (
        <button
          type="button"
          className={`text-xs ${colors.textMuted} hover:underline`}
          onClick={() => setFilterFields([])}
        >
          Clear filters
        </button>
      ) : null}
    </div>
  );
}

function InspectorPanel({
  schema,
  onChangeSchema,
  formName,
  isActive,
  onIsActiveChange,
  description,
  onDescriptionChange,
  selectedFieldId,
  selectedSectionId,
  onChangeField,
  onChangeSection,
}: {
  schema: EFormSchema;
  onChangeSchema: (next: EFormSchema) => void;
  formName: string;
  isActive: boolean;
  onIsActiveChange: (next: boolean) => void;
  description: string;
  onDescriptionChange: (next: string) => void;
  selectedFieldId: string | null;
  selectedSectionId: string | null;
  onChangeField: (field: EFormFieldSchema) => void;
  onChangeSection: (section: EFormSectionSchema) => void;
}) {
  const selectedField = schema.sections.flatMap((s) => s.fields).find((f) => f.id === selectedFieldId);
  const selectedSection = schema.sections.find((s) => s.id === selectedSectionId);
  const selectionTabLabel = selectedField
    ? 'Field'
    : selectedSection
      ? 'Section'
      : 'Selection';

  const [tab, setTab] = useState('form');

  useEffect(() => {
    if (selectedFieldId || selectedSectionId) {
      setTab('selection');
    }
  }, [selectedFieldId, selectedSectionId]);

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 px-3 pt-1 pb-2">
        <TabsList variant="pill" className="w-full">
          <TabsTrigger
            value="form"
            appearance="pill"
            size="sm"
            colorScheme="sky"
            className="flex-1 justify-center min-w-0"
          >
            Form
          </TabsTrigger>
          <TabsTrigger
            value="list"
            appearance="pill"
            size="sm"
            colorScheme="sky"
            className="flex-1 justify-center min-w-0"
          >
            List
          </TabsTrigger>
          <TabsTrigger
            value="selection"
            appearance="pill"
            size="sm"
            colorScheme="sky"
            className="flex-1 justify-center min-w-0"
            count={selectedField || selectedSection ? 1 : undefined}
            countTone="onColor"
          >
            {selectionTabLabel}
          </TabsTrigger>
        </TabsList>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        <TabsContent value="form" className="!mt-0">
          <FormSettingsPanel
            schema={schema}
            onChange={onChangeSchema}
            formName={formName}
            isActive={isActive}
            onIsActiveChange={onIsActiveChange}
            description={description}
            onDescriptionChange={onDescriptionChange}
          />
        </TabsContent>
        <TabsContent value="list" className="!mt-0">
          <ListColumnsPanel schema={schema} onChange={onChangeSchema} />
          <ListFiltersPanel schema={schema} onChange={onChangeSchema} />
        </TabsContent>
        <TabsContent value="selection" className="!mt-0">
          <PropertyPanel
            schema={schema}
            selectedFieldId={selectedFieldId}
            selectedSectionId={selectedSectionId}
            onChangeField={onChangeField}
            onChangeSection={onChangeSection}
          />
        </TabsContent>
      </div>
    </Tabs>
  );
}

function menuNodeAnchorPaths(node: SidebarMenuItem): string[] {
  const paths: string[] = [];
  if (typeof node.path === 'string' && node.path) paths.push(node.path);
  for (const v of node.views ?? []) {
    if (typeof v.path === 'string' && v.path) paths.push(v.path);
  }
  return paths;
}

function menuNodeContainsAnchor(node: SidebarMenuItem, anchorPath: string | null | undefined): boolean {
  if (!anchorPath) return false;
  if (menuNodeAnchorPaths(node).includes(anchorPath)) return true;
  return (node.children ?? []).some((c) => menuNodeContainsAnchor(c, anchorPath));
}

function SidebarPlacementEditorModal({
  isOpen,
  onClose,
  schema,
  formName,
  onChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  schema: EFormSchema;
  formName: string;
  onChange: (next: EFormSchema) => void;
}) {
  const { colors } = useTheme();
  const settings = schema.settings ?? {};
  const normalized = normalizeSidebarPlacements(settings.sidebarPlacements);
  const keepInCatalog = normalized.some((p) => p.mode === 'catalog_only');
  const shortcutPlacements = normalized.filter(
    (p): p is Extract<EFormSidebarPlacement, { mode: 'shortcut' }> => p.mode === 'shortcut',
  );
  const primaryShortcut = shortcutPlacements[0] ?? null;

  const { user, can, permissionsLoaded } = useAuth();
  const { entitlements } = useModuleEntitlements();
  const vesselAccountId = useAccountVesselScope();

  const canView = useMemo(() => {
    if (!(permissionsLoaded || user?.role === 'ADMIN')) return undefined;
    return (moduleKey: string) => can(moduleKey, 'view');
  }, [can, permissionsLoaded, user?.role]);

  const sidebarTree = useMemo(
    () => getSidebarItems(canView, vesselAccountId, entitlements),
    [canView, vesselAccountId, entitlements],
  );

  const pathLabels = useMemo(() => collectSidebarPathLabels(sidebarTree), [sidebarTree]);
  const [hoverTarget, setHoverTarget] = useState<{
    anchorPath: string;
    insert: 'before' | 'after';
  } | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) {
      setHoverTarget(null);
      setExpandedKeys({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !primaryShortcut?.anchorPath) return;
    setExpandedKeys((prev) => {
      const next = { ...prev };
      const walk = (nodes: SidebarMenuItem[], parentKey = '') => {
        nodes.forEach((node, idx) => {
          if (node.sectionLabel) return;
          const key = `${parentKey}/${node.name}-${idx}`;
          if ((node.children?.length ?? 0) > 0) {
            if (menuNodeContainsAnchor(node, primaryShortcut.anchorPath)) {
              next[key] = true;
            }
            walk(node.children ?? [], key);
          }
        });
      };
      walk(sidebarTree);
      return next;
    });
  }, [isOpen, primaryShortcut?.anchorPath, sidebarTree]);

  const writePlacements = (
    nextShortcuts: Extract<EFormSidebarPlacement, { mode: 'shortcut' }>[],
    catalog: boolean,
  ) => {
    onChange({
      ...schema,
      settings: {
        ...settings,
        sidebarPlacements: buildSidebarPlacements(nextShortcuts, catalog),
      },
    });
  };

  const applyCatalogOnly = () => {
    setHoverTarget(null);
    writePlacements([], true);
  };

  const applyShortcutPlacement = (anchorPath: string, insert: 'before' | 'after') => {
    setHoverTarget(null);
    writePlacements(
      [{ mode: 'shortcut', anchorPath, insert, sortOrder: Date.now() }],
      keepInCatalog || !shortcutPlacements.length,
    );
  };

  const removeShortcuts = () => {
    setHoverTarget(null);
    writePlacements([], keepInCatalog);
  };

  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const ShortcutPreviewRow = ({ level, insert }: { level: number; insert: 'before' | 'after' }) => (
    <div
      className={cn(
        'mx-1 my-0.5 flex items-center gap-2 rounded-md border border-dashed px-2 py-1.5 text-sm',
        'border-sky-400/70 bg-sky-50 text-sky-900 dark:border-sky-500/50 dark:bg-sky-950/40 dark:text-sky-100',
      )}
      style={{ marginLeft: `${8 + level * 12}px` }}
    >
      <span className="rounded bg-sky-600/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-200">
        {insert}
      </span>
      <DocumentTextIcon className="w-4 h-4 shrink-0 opacity-80" />
      <span className="min-w-0 truncate font-medium">{formName || 'E-Form'}</span>
      <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wide opacity-70">Shortcut</span>
    </div>
  );

  const SidebarNode: React.FC<{
    node: SidebarMenuItem;
    level: number;
    nodeKey: string;
  }> = ({ node, level, nodeKey }) => {
    const paths = menuNodeAnchorPaths(node);
    const anchorPath = paths[0];
    const hasAnchor = Boolean(anchorPath);
    const hasChildren = (node.children?.length ?? 0) > 0;
    const indent = 8 + level * 12;
    const isExpanded = hasChildren ? expandedKeys[nodeKey] === true : false;

    const placedHere =
      hasAnchor && primaryShortcut && paths.includes(primaryShortcut.anchorPath)
        ? primaryShortcut
        : null;
    const hoveringHere =
      hasAnchor && hoverTarget && paths.includes(hoverTarget.anchorPath) ? hoverTarget : null;
    const previewInsert = hoveringHere?.insert ?? placedHere?.insert ?? null;
    const isAnchor = Boolean(placedHere);

    if (node.sectionLabel) {
      return (
        <div className={cn('px-2 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold', colors.textMuted)}>
          {node.sectionLabel}
        </div>
      );
    }

    const rowContent = (
      <div className="flex items-center gap-2 min-w-0 w-full">
        {hasChildren ? (
          <button
            type="button"
            className={cn(
              'shrink-0 rounded p-0.5 transition-colors',
              colors.textMuted,
              'hover:bg-slate-200/70 dark:hover:bg-slate-700',
            )}
            title={isExpanded ? 'Collapse' : 'Expand'}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(nodeKey);
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : (
              <ChevronRightIcon className="w-3.5 h-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        {node.icon ? (
          <span className={cn('shrink-0 flex items-center justify-center w-4 h-4', colors.textMuted)}>
            {node.icon}
          </span>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <span className="truncate flex-1 text-left">{node.name}</span>
        {isAnchor ? (
          <span className="shrink-0 rounded bg-sky-600/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-200">
            Anchor
          </span>
        ) : null}
      </div>
    );

    return (
      <div>
        {previewInsert === 'before' ? <ShortcutPreviewRow level={level} insert="before" /> : null}
        <div
          className={cn(
            'mx-1 rounded-md px-2 py-1.5 text-sm select-none transition-colors',
            hasAnchor ? 'cursor-copy' : hasChildren ? 'cursor-pointer' : 'opacity-80',
            isAnchor
              ? 'bg-sky-100/80 ring-1 ring-sky-400/60 dark:bg-sky-950/50 dark:ring-sky-500/40'
              : hoveringHere
                ? 'bg-slate-100 dark:bg-slate-800'
                : 'hover:bg-slate-50 dark:hover:bg-slate-800/60',
          )}
          style={{ paddingLeft: `${indent}px` }}
          title={
            hasAnchor
              ? `Drop to place near ${pathLabels[anchorPath!] ?? anchorPath}`
              : hasChildren
                ? isExpanded
                  ? 'Collapse'
                  : 'Expand'
                : undefined
          }
          onClick={
            hasChildren && !hasAnchor
              ? () => toggleExpanded(nodeKey)
              : undefined
          }
          onDragOver={
            hasAnchor
              ? (e) => {
                  if (!e.dataTransfer.types?.includes('text/plain')) return;
                  e.preventDefault();
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const insert: 'before' | 'after' =
                    e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                  if (
                    !hoverTarget ||
                    hoverTarget.anchorPath !== anchorPath ||
                    hoverTarget.insert !== insert
                  ) {
                    setHoverTarget({ anchorPath: anchorPath!, insert });
                  }
                }
              : undefined
          }
          onDragLeave={
            hasAnchor
              ? () => {
                  setHoverTarget((prev) =>
                    prev?.anchorPath === anchorPath ? null : prev,
                  );
                }
              : undefined
          }
          onDrop={
            hasAnchor
              ? (e) => {
                  e.preventDefault();
                  const token = e.dataTransfer.getData('text/plain');
                  if (token !== 'eform-shortcut') return;
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  const insert: 'before' | 'after' =
                    e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                  applyShortcutPlacement(anchorPath!, insert);
                }
              : undefined
          }
        >
          {rowContent}
        </div>
        {previewInsert === 'after' ? <ShortcutPreviewRow level={level} insert="after" /> : null}
        {hasChildren && isExpanded
          ? node.children!.map((c, idx) => (
              <SidebarNode
                key={`${c.name}-${idx}-${c.path ?? c.views?.[0]?.path ?? ''}`}
                node={c}
                level={level + 1}
                nodeKey={`${nodeKey}/${c.name}-${idx}`}
              />
            ))
          : null}
      </div>
    );
  };

  const placementSummary = primaryShortcut
    ? `${primaryShortcut.insert === 'before' ? 'Before' : 'After'} · ${
        pathLabels[primaryShortcut.anchorPath] ?? primaryShortcut.anchorPath
      }`
    : keepInCatalog
      ? 'Only in E-Forms catalog'
      : 'Hidden from sidebar';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader>
        <ModalTitle>Sidebar placement · {formName || 'E-Form'}</ModalTitle>
      </ModalHeader>
      <ModalContent className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-4 space-y-3">
            <div className={cn('rounded-lg border p-3 space-y-3', colors.border)}>
              <div>
                <p className="text-sm font-medium">Drag the shortcut</p>
                <p className={`text-xs mt-1 ${colors.textMuted}`}>
                  Expand a module, then drop on a menu row. Top half = before, bottom half = after.
                </p>
              </div>

              <div
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', 'eform-shortcut');
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                className={cn(
                  'inline-flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 cursor-grab select-none',
                  'bg-sky-50 border-sky-300 text-sky-900 dark:bg-sky-950/40 dark:border-sky-700 dark:text-sky-100',
                )}
              >
                <GripVerticalIcon className="w-4 h-4 shrink-0 opacity-70" />
                <DocumentTextIcon className="w-4 h-4 shrink-0 opacity-80" />
                <span className="flex-1 min-w-0 text-sm font-medium truncate">
                  {formName || 'E-Form'}
                </span>
                <Badge variant="secondary">{primaryShortcut ? 'Placed' : 'Not placed'}</Badge>
              </div>

              <div className={cn('rounded-lg border px-3 py-2 space-y-1', colors.border, colors.background)}>
                <p className={`text-[10px] uppercase tracking-wide ${colors.textMuted}`}>Current placement</p>
                <p className="text-sm font-medium">{formName || 'E-Form'}</p>
                <p className={`text-xs ${colors.textMuted}`}>{placementSummary}</p>
                {keepInCatalog && primaryShortcut ? (
                  <p className={`text-xs ${colors.textMuted}`}>Also kept in E-Forms catalog</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                    !primaryShortcut && keepInCatalog
                      ? 'border-sky-400 bg-sky-50 dark:bg-sky-950/40'
                      : colors.border,
                  )}
                  onClick={applyCatalogOnly}
                  onDragOver={(e) => {
                    if (e.dataTransfer.types?.includes('text/plain')) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.getData('text/plain') !== 'eform-shortcut') return;
                    applyCatalogOnly();
                  }}
                >
                  E-Forms catalog only
                </button>

                <div className="flex items-start gap-2 px-1">
                  <Switch
                    checked={keepInCatalog}
                    onChange={(e) => writePlacements(shortcutPlacements, e.target.checked)}
                  />
                  <div className="text-xs">
                    Also show under E-Forms
                    {primaryShortcut ? (
                      <p className={cn('mt-0.5', colors.textMuted)}>
                        Turn off to remove this form from the E-Forms sidebar group.
                      </p>
                    ) : null}
                  </div>
                </div>

                {primaryShortcut ? (
                  <button
                    type="button"
                    className={cn('w-full rounded-lg border px-3 py-2 text-sm', colors.border, colors.textMuted)}
                    onClick={removeShortcuts}
                  >
                    Clear module shortcut
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div
            className={cn(
              'lg:col-span-8 min-h-[28rem] max-h-[55vh] overflow-y-auto rounded-lg border p-2',
              colors.border,
            )}
          >
            <div className="sticky top-0 z-10 mb-1 flex items-center justify-between gap-2 rounded-md px-2 py-2 backdrop-blur-sm bg-white/80 dark:bg-slate-950/70">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide">Sidebar preview</p>
                <p className={`text-[11px] ${colors.textMuted}`}>
                  Expand modules · blue row = your shortcut
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={cn('text-[11px] underline-offset-2 hover:underline', colors.textMuted)}
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    const walk = (nodes: SidebarMenuItem[], parentKey = '') => {
                      nodes.forEach((node, idx) => {
                        if (node.sectionLabel) return;
                        const key = `${parentKey}/${node.name}-${idx}`;
                        if ((node.children?.length ?? 0) > 0) {
                          next[key] = true;
                          walk(node.children ?? [], key);
                        }
                      });
                    };
                    walk(sidebarTree);
                    setExpandedKeys(next);
                  }}
                >
                  Expand all
                </button>
                <button
                  type="button"
                  className={cn('text-[11px] underline-offset-2 hover:underline', colors.textMuted)}
                  onClick={() => setExpandedKeys({})}
                >
                  Collapse all
                </button>
                {primaryShortcut ? (
                  <Badge variant="success">Placed</Badge>
                ) : (
                  <Badge variant="secondary">Not placed</Badge>
                )}
              </div>
            </div>
            <div className="space-y-0.5 pb-2">
              {sidebarTree.map((root, idx) => (
                <SidebarNode
                  key={`${root.name}-${idx}-${root.path ?? ''}`}
                  node={root}
                  level={0}
                  nodeKey={`/${root.name}-${idx}`}
                />
              ))}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function SubformColumnsModal({
  isOpen,
  onClose,
  field,
  onChangeField,
}: {
  isOpen: boolean;
  onClose: () => void;
  field: EFormFieldSchema;
  onChangeField: (field: EFormFieldSchema) => void;
}) {
  const { colors } = useTheme();
  const columns = field.fields ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalHeader>
        <ModalTitle>Sub-form columns · {field.label || field.key}</ModalTitle>
      </ModalHeader>
      <ModalContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Row editor</Label>
            <Select
              value={field.rowEditor === 'inline' ? 'inline' : 'modal'}
              onChange={(e) => {
                const v = e.target.value === 'inline' ? 'inline' : 'modal';
                onChangeField({ ...field, rowEditor: v });
              }}
              options={[
                { value: 'modal', label: 'Modal form' },
                { value: 'inline', label: 'Inline table' },
              ]}
              showClearButton={false}
            />
            <p className={`text-xs mt-1 ${colors.textMuted}`}>
              How fillers add and edit rows.
            </p>
          </div>
          <div>
            <Label>Min rows</Label>
            <TextInput
              type="number"
              value={field.minRows != null ? String(field.minRows) : '0'}
              onChange={(e) => {
                const n = e.target.value === '' ? 0 : Number(e.target.value);
                onChangeField({
                  ...field,
                  minRows: Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0,
                });
              }}
            />
          </div>
          <div>
            <Label>Max rows</Label>
            <TextInput
              type="number"
              value={field.maxRows != null ? String(field.maxRows) : '50'}
              onChange={(e) => {
                const n = e.target.value === '' ? 50 : Number(e.target.value);
                onChangeField({
                  ...field,
                  maxRows: Number.isFinite(n) && n >= 1 ? Math.floor(n) : 50,
                });
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <div>
            <Label>Columns</Label>
            <p className={`text-xs ${colors.textMuted}`}>
              {columns.length} column{columns.length === 1 ? '' : 's'} · one nesting level
            </p>
          </div>
          <Select
            value=""
            onChange={(e) => {
              const t = e.target.value as EFormSubformNestedType;
              if (!t) return;
              const nested = createSubformNestedField(t);
              onChangeField({
                ...field,
                fields: [...(field.fields ?? []), nested],
              });
            }}
            options={[
              { value: '', label: '+ Add column…' },
              ...EFORM_SUBFORM_NESTED_TYPES.map((t) => ({
                value: t,
                label: t.charAt(0).toUpperCase() + t.slice(1),
              })),
            ]}
            className="min-w-[10rem]"
          />
        </div>

        {columns.length === 0 ? (
          <p className={`text-sm py-6 text-center ${colors.textMuted}`}>
            Add at least one column for this sub-form.
          </p>
        ) : (
          <ul className="space-y-3">
            {columns.map((nf, index) => (
              <li
                key={nf.id}
                className={cn(
                  'rounded-xl border p-3 space-y-2',
                  colors.border,
                  colors.background,
                )}
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  <span
                    className={cn(
                      'text-[10px] font-medium uppercase px-1.5 py-0.5 rounded',
                      FIELD_TYPE_BADGE[nf.type],
                    )}
                  >
                    {nf.type}
                  </span>
                  <span className={`text-xs tabular-nums ${colors.textMuted}`}>#{index + 1}</span>
                  <div className="flex-1" />
                  <button
                    type="button"
                    className={cn(
                      'p-1.5 text-xs rounded-lg',
                      colors.textMuted,
                      'hover:bg-slate-100 dark:hover:bg-slate-800',
                      index === 0 && 'opacity-30 pointer-events-none',
                    )}
                    title="Move up"
                    onClick={() => {
                      if (index === 0) return;
                      const next = [...(field.fields ?? [])];
                      const tmp = next[index - 1];
                      next[index - 1] = next[index];
                      next[index] = tmp;
                      onChangeField({ ...field, fields: next });
                    }}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={cn(
                      'p-1.5 text-xs rounded-lg',
                      colors.textMuted,
                      'hover:bg-slate-100 dark:hover:bg-slate-800',
                      index === columns.length - 1 && 'opacity-30 pointer-events-none',
                    )}
                    title="Move down"
                    onClick={() => {
                      if (index >= columns.length - 1) return;
                      const next = [...(field.fields ?? [])];
                      const tmp = next[index + 1];
                      next[index + 1] = next[index];
                      next[index] = tmp;
                      onChangeField({ ...field, fields: next });
                    }}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className={`p-1.5 rounded-lg ${colors.textMuted} hover:bg-slate-100 dark:hover:bg-slate-800`}
                    title="Duplicate"
                    onClick={() => {
                      const copy = duplicateField(nf);
                      const next = [...(field.fields ?? [])];
                      next.splice(index + 1, 0, copy);
                      onChangeField({ ...field, fields: next });
                    }}
                  >
                    <CopyIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                    title="Remove"
                    onClick={() => {
                      onChangeField({
                        ...field,
                        fields: (field.fields ?? []).filter((f) => f.id !== nf.id),
                      });
                    }}
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label>Label</Label>
                    <TextInput
                      value={nf.label}
                      onChange={(e) => {
                        const next = (field.fields ?? []).map((f) =>
                          f.id === nf.id ? { ...f, label: e.target.value } : f,
                        );
                        onChangeField({ ...field, fields: next });
                      }}
                      placeholder="Label"
                    />
                  </div>
                  <div>
                    <Label>Key</Label>
                    <TextInput
                      value={nf.key}
                      onChange={(e) => {
                        const key = e.target.value.replace(/[^a-zA-Z0-9_]/g, '_');
                        const next = (field.fields ?? []).map((f) =>
                          f.id === nf.id ? { ...f, key } : f,
                        );
                        onChangeField({ ...field, fields: next });
                      }}
                      placeholder="Key"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={Boolean(nf.required)}
                    onChange={(e) => {
                      const next = (field.fields ?? []).map((f) =>
                        f.id === nf.id ? { ...f, required: e.target.checked } : f,
                      );
                      onChangeField({ ...field, fields: next });
                    }}
                  />
                  Required
                </label>
                {(nf.type === 'select' || nf.type === 'radio') && (
                  <div>
                    <Label>Options (label|value per line)</Label>
                    <Textarea
                      rows={3}
                      className="mt-1"
                      value={(nf.options ?? []).map((o) => `${o.label}|${o.value}`).join('\n')}
                      onChange={(e) => {
                        const options = e.target.value
                          .split('\n')
                          .map((line) => line.trim())
                          .filter(Boolean)
                          .map((line) => {
                            const [label, value] = line.split('|');
                            return {
                              label: (label ?? '').trim(),
                              value: (value ?? label ?? '').trim(),
                            };
                          });
                        const next = (field.fields ?? []).map((f) =>
                          f.id === nf.id
                            ? { ...f, optionsSource: 'static' as const, options }
                            : f,
                        );
                        onChangeField({ ...field, fields: next });
                      }}
                      placeholder="label|value per line"
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="default" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function PropertyPanel({
  schema,
  selectedFieldId,
  selectedSectionId,
  onChangeField,
  onChangeSection,
}: {
  schema: EFormSchema;
  selectedFieldId: string | null;
  selectedSectionId: string | null;
  onChangeField: (field: EFormFieldSchema) => void;
  onChangeSection: (section: EFormSectionSchema) => void;
}) {
  const { colors } = useTheme();
  const [subformModalOpen, setSubformModalOpen] = useState(false);
  const field = schema.sections.flatMap((s) => s.fields).find((f) => f.id === selectedFieldId);
  const section = schema.sections.find((s) => s.id === selectedSectionId);
  const allFields = flattenSchemaFields(schema);
  const otherFieldOptions = allFields
    .filter((f) => f.id !== field?.id && !isEFormDisplayField(f))
    .map((f) => ({ value: f.key, label: `${f.label} (${f.key})` }));

  useEffect(() => {
    setSubformModalOpen(false);
  }, [selectedFieldId]);

  if (field) {
    const needsOptions = field.type === 'select' || field.type === 'radio' || field.type === 'checklist';
    const validation = field.validation ?? {};
    const subformCols = field.fields ?? [];
    const isDisplay = isEFormDisplayField(field);
    return (
      <div className="space-y-3">
        <PanelHeading
          title="Field properties"
          hint={isDisplay ? 'Layout · not filled in' : `Editing · ${field.type}`}
        />
        {isDisplay ? (
          field.type === 'spacer' ? (
            <div>
              <Label>Height</Label>
              <Select
                value={String(eformSpacerHeight(field))}
                onChange={(e) =>
                  onChangeField({ ...field, defaultValue: Number(e.target.value) || 24 })
                }
                options={[
                  ...EFORM_SPACER_PRESETS.map((p) => ({
                    value: String(p.value),
                    label: p.label,
                  })),
                  ...(!EFORM_SPACER_PRESETS.some((p) => p.value === eformSpacerHeight(field))
                    ? [
                        {
                          value: String(eformSpacerHeight(field)),
                          label: `Custom · ${eformSpacerHeight(field)}px`,
                        },
                      ]
                    : []),
                ]}
              />
              <p className={`text-xs mt-1 ${colors.textMuted}`}>
                Vertical gap on the filled form (does not collect a value).
              </p>
            </div>
          ) : field.type === 'richtext' ? (
            <>
              <div>
                <Label>Caption</Label>
                <TextInput
                  value={field.label}
                  onChange={(e) => onChangeField({ ...field, label: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div onKeyDown={(e) => e.stopPropagation()}>
                {renderField({
                    field: {
                      name: `${field.id}_content`,
                      label: 'Content',
                      type: 'richtext',
                    },
                    value: typeof field.defaultValue === 'string' ? field.defaultValue : '',
                    onChange: (_name, value) =>
                      onChangeField({ ...field, defaultValue: String(value ?? '') }),
                    getFormData: () => ({}),
                  })}
              </div>
            </>
          ) : (
            <div>
              <Label>Content</Label>
              {field.type === 'textarea' ? (
                <Textarea
                  className="mt-1 min-h-[120px]"
                  rows={8}
                  value={field.label}
                  onChange={(e) => onChangeField({ ...field, label: e.target.value })}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Printed text as it appears on the form. Enter starts a new line."
                />
              ) : (
                <TextInput
                  className="mt-1"
                  value={field.label}
                  onChange={(e) => onChangeField({ ...field, label: e.target.value })}
                  placeholder={field.type === 'heading' ? 'Heading' : 'Printed text'}
                />
              )}
            </div>
          )
        ) : (
          <div>
            <Label>Label</Label>
            <TextInput value={field.label} onChange={(e) => onChangeField({ ...field, label: e.target.value })} />
          </div>
        )}
        <div>
          <Label>Key</Label>
          <TextInput
            value={field.key}
            onChange={(e) =>
              onChangeField({
                ...field,
                key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'),
              })
            }
          />
        </div>
        {!isDisplay ? (
          <>
        {field.type !== 'photo' ? (
        <div>
          <Label>Placeholder</Label>
          <TextInput
            value={field.placeholder ?? ''}
            onChange={(e) => onChangeField({ ...field, placeholder: e.target.value })}
          />
        </div>
        ) : null}
        <div>
          <Label>Help text</Label>
          <TextInput
            value={field.helpText ?? ''}
            onChange={(e) => onChangeField({ ...field, helpText: e.target.value })}
          />
        </div>
          </>
        ) : null}
        {field.type === 'file' ? (
          <div className="space-y-2 rounded border border-dashed p-2">
            <Label>File options</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={Boolean(field.multiple)}
                onChange={(e) => onChangeField({ ...field, multiple: e.target.checked })}
              />
              <Label>Allow multiple files</Label>
            </div>
            <div>
              <Label>Accept</Label>
              <TextInput
                value={field.accept ?? ''}
                onChange={(e) => onChangeField({ ...field, accept: e.target.value })}
                placeholder=".pdf,image/*"
              />
              <p className={`text-xs mt-1 ${colors.textMuted}`}>
                Optional HTML accept filter (e.g. .pdf,image/*)
              </p>
            </div>
          </div>
        ) : null}
        {field.type === 'photo' ? (
          <div className="space-y-2 rounded border border-dashed p-2">
            <Label>Photo</Label>
            <p className={`text-xs ${colors.textMuted}`}>
              Round avatar control, same style as the crew file photo. Fillers upload one image.
            </p>
          </div>
        ) : null}
        {field.type === 'signature' ? (
          <div className="space-y-2 rounded border border-dashed p-2">
            <Label>Signature options</Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={Boolean(field.signatureWithStamp)}
                onChange={(e) =>
                  onChangeField({ ...field, signatureWithStamp: e.target.checked })
                }
              />
              <Label>Include stamp / seal</Label>
            </div>
            <p className={`text-xs ${colors.textMuted}`}>
              Stamp sits behind the signature on the same pad. Fillers can pick a vessel’s
              uploaded stamp or upload an image.
            </p>
          </div>
        ) : null}
        {field.type === 'subform' ? (
          <>
            <div className={cn('rounded-xl border p-3 space-y-2.5', colors.border, colors.surface)}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Label>Sub-form rows</Label>
                  <p className={`text-xs mt-0.5 ${colors.textMuted}`}>
                    {subformCols.length} column{subformCols.length === 1 ? '' : 's'}
                    {' · '}
                    {field.rowEditor === 'inline' ? 'Inline' : 'Modal'}
                    {' · '}
                    {field.minRows ?? 0}–{field.maxRows ?? 50} rows
                  </p>
                </div>
              </div>
              {subformCols.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {subformCols.map((nf) => (
                    <span
                      key={nf.id}
                      className={cn(
                        'rounded border px-1.5 py-0.5 text-[10px]',
                        colors.border,
                        colors.textMuted,
                      )}
                      title={`${nf.type}${nf.required ? ' · required' : ''}`}
                    >
                      {nf.label || nf.key}
                    </span>
                  ))}
                </div>
              ) : (
                <p className={`text-xs ${colors.textMuted}`}>No columns yet — configure to continue.</p>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full inline-flex items-center justify-center gap-1.5"
                onClick={() => setSubformModalOpen(true)}
              >
                <PencilIcon className="w-3.5 h-3.5" />
                Configure columns
              </Button>
            </div>
            <SubformColumnsModal
              isOpen={subformModalOpen}
              onClose={() => setSubformModalOpen(false)}
              field={field}
              onChangeField={onChangeField}
            />
          </>
        ) : null}
        <div>
          <Label>Width</Label>
          <Select
            value={String(resolveFieldColSpan(field))}
            onChange={(e) =>
              onChangeField({
                ...field,
                colSpan: normalizeFieldColSpan(Number(e.target.value)),
              })
            }
            options={EFORM_COL_SPANS.map((n) => ({
              value: String(n),
              label: colSpanOptionLabel(n),
            }))}
          />
          <p className={`text-xs mt-1 ${colors.textMuted}`}>
            12-unit row. 8+4 is 2/3 + 1/3; 6+6 is half + half.
          </p>
        </div>
        {!isDisplay ? (
          <>
        <div>
          <Label>Default value</Label>
          {field.type === 'file' ||
          field.type === 'photo' ||
          field.type === 'subform' ||
          field.type === 'signature' ? (
            <p className={`text-xs ${colors.textMuted}`}>
              {field.type === 'file'
                ? 'File fields have no default value.'
                : field.type === 'photo'
                  ? 'Upload a photo when filling. Same round control as the crew file.'
                : field.type === 'signature'
                  ? 'Draw or upload a signature when filling (stamp sits behind ink if enabled).'
                  : 'Sub-form rows start empty (use Add row when filling).'}
            </p>
          ) : (
            <>
              <TextInput
                value={
                  field.defaultValue == null
                    ? ''
                    : Array.isArray(field.defaultValue)
                      ? field.defaultValue.join(',')
                      : String(field.defaultValue)
                }
                onChange={(e) => onChangeField({ ...field, defaultValue: e.target.value })}
                placeholder="Use __today__ or __userName__"
              />
              <p className={`text-xs mt-1 ${colors.textMuted}`}>Hint: __today__ or __userName__</p>
            </>
          )}
        </div>
        <div className="space-y-2 rounded border border-dashed p-2">
          <Label>Validation</Label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>minLength</Label>
              <TextInput
                type="number"
                value={validation.minLength != null ? String(validation.minLength) : ''}
                onChange={(e) => {
                  const n = e.target.value === '' ? undefined : Number(e.target.value);
                  onChangeField({
                    ...field,
                    validation: { ...validation, minLength: Number.isFinite(n) ? n : undefined },
                  });
                }}
              />
            </div>
            <div>
              <Label>maxLength</Label>
              <TextInput
                type="number"
                value={validation.maxLength != null ? String(validation.maxLength) : ''}
                onChange={(e) => {
                  const n = e.target.value === '' ? undefined : Number(e.target.value);
                  onChangeField({
                    ...field,
                    validation: { ...validation, maxLength: Number.isFinite(n) ? n : undefined },
                  });
                }}
              />
            </div>
            <div>
              <Label>min</Label>
              <TextInput
                type="number"
                value={validation.min != null ? String(validation.min) : ''}
                onChange={(e) => {
                  const n = e.target.value === '' ? undefined : Number(e.target.value);
                  onChangeField({
                    ...field,
                    validation: { ...validation, min: Number.isFinite(n) ? n : undefined },
                  });
                }}
              />
            </div>
            <div>
              <Label>max</Label>
              <TextInput
                type="number"
                value={validation.max != null ? String(validation.max) : ''}
                onChange={(e) => {
                  const n = e.target.value === '' ? undefined : Number(e.target.value);
                  onChangeField({
                    ...field,
                    validation: { ...validation, max: Number.isFinite(n) ? n : undefined },
                  });
                }}
              />
            </div>
          </div>
          <div>
            <Label>pattern</Label>
            <TextInput
              value={validation.pattern ?? ''}
              onChange={(e) =>
                onChangeField({
                  ...field,
                  validation: { ...validation, pattern: e.target.value || undefined },
                })
              }
            />
          </div>
          <div>
            <Label>message</Label>
            <TextInput
              value={validation.message ?? ''}
              onChange={(e) =>
                onChangeField({
                  ...field,
                  validation: { ...validation, message: e.target.value || undefined },
                })
              }
            />
          </div>
        </div>
          </>
        ) : null}
        <VisibleWhenEditor
          value={field.visibleWhen}
          fieldOptions={otherFieldOptions}
          onChange={(visibleWhen) => onChangeField({ ...field, visibleWhen })}
        />
        {!isDisplay ? (
          <>
        <div className="flex items-center gap-2">
          <Switch
            checked={Boolean(field.required)}
            onChange={(e) => onChangeField({ ...field, required: e.target.checked })}
          />
          <span className="text-sm">Required</span>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={Boolean(field.readOnly)}
            onChange={(e) => onChangeField({ ...field, readOnly: e.target.checked })}
          />
          <span className="text-sm">Read-only</span>
        </div>
        <div>
          <Label>Calculated from</Label>
          <Select
            value={field.calculatedFrom ?? ''}
            onChange={(e) =>
              onChangeField({
                ...field,
                calculatedFrom: e.target.value || undefined,
              })
            }
            options={[{ value: '', label: '— None —' }, ...otherFieldOptions]}
          />
        </div>
        {needsOptions ? (
          <div className="space-y-3">
            {(field.type === 'select' || field.type === 'radio') && (
              <div>
                <Label>Options source</Label>
                <Select
                  value={(field.optionsSource === 'reference' ? 'reference' : 'static') as string}
                  onChange={(e) => {
                    const next = e.target.value as EFormOptionsSource;
                    if (next === 'reference') {
                      onChangeField({
                        ...field,
                        optionsSource: 'reference',
                        referenceKey: field.referenceKey || EFORM_REFERENCE_SOURCES[0]?.key,
                        options: undefined,
                      });
                    } else {
                      onChangeField({
                        ...field,
                        optionsSource: 'static',
                        referenceKey: undefined,
                        options: field.options?.length
                          ? field.options
                          : [
                              { value: 'option_1', label: 'Option 1' },
                              { value: 'option_2', label: 'Option 2' },
                            ],
                      });
                    }
                  }}
                  options={[
                    { value: 'static', label: 'Manual options' },
                    { value: 'reference', label: 'Reference data' },
                  ]}
                />
              </div>
            )}
            {field.type !== 'checklist' && field.optionsSource === 'reference' ? (
              <div>
                <Label>Reference list</Label>
                <Select
                  value={field.referenceKey ?? ''}
                  onChange={(e) =>
                    onChangeField({
                      ...field,
                      optionsSource: 'reference',
                      referenceKey: e.target.value || undefined,
                    })
                  }
                  options={EFORM_REFERENCE_SOURCES.map((s) => ({
                    value: s.key,
                    label: `${s.category}: ${s.label}`,
                  }))}
                />
                <p className={`text-xs mt-1 ${colors.textMuted}`}>
                  Options load from Atlas reference data when the form is filled.
                </p>
              </div>
            ) : (
              <div>
                <Label>Options (label|value per line)</Label>
                <Textarea
                  rows={5}
                  value={(field.options ?? []).map((o) => `${o.label}|${o.value}`).join('\n')}
                  onChange={(e) => {
                    const options = e.target.value
                      .split('\n')
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line) => {
                        const [label, value] = line.split('|');
                        return {
                          label: (label ?? '').trim(),
                          value: (value ?? label ?? '').trim(),
                        };
                      });
                    onChangeField({ ...field, optionsSource: 'static', options });
                  }}
                />
              </div>
            )}
          </div>
        ) : null}
          </>
        ) : null}
      </div>
    );
  }

  if (section) {
    const columns = normalizeSectionColumns(section.columns);
    const sectionFieldOptions = allFields
      .filter((f) => !section.fields.some((sf) => sf.id === f.id))
      .map((f) => ({ value: f.key, label: `${f.label} (${f.key})` }));
    // Allow referencing any field including within section for visibility
    const visibilityOptions = allFields.map((f) => ({
      value: f.key,
      label: `${f.label} (${f.key})`,
    }));
    return (
      <div className="space-y-3">
        <PanelHeading title="Section properties" hint="Layout and visibility for this section" />
        <div>
          <Label>Heading</Label>
          <TextInput
            value={section.heading}
            onChange={(e) => onChangeSection({ ...section, heading: e.target.value })}
          />
        </div>
        <div>
          <Label>Subheading</Label>
          <TextInput
            value={section.subHeading ?? ''}
            onChange={(e) => onChangeSection({ ...section, subHeading: e.target.value })}
          />
        </div>
        <div>
          <Label>Columns</Label>
          <Select
            value={String(columns)}
            onChange={(e) =>
              onChangeSection({
                ...section,
                columns: normalizeSectionColumns(Number(e.target.value)) as EFormSectionColumns,
              })
            }
            options={EFORM_SECTION_COLUMNS.map((n) => ({
              value: String(n),
              label:
                n === 1
                  ? '1 column · new fields full'
                  : `${n} columns · new fields ${defaultColSpanForSection(n)}/12`,
            }))}
          />
          <p className={`text-xs mt-1 ${colors.textMuted}`}>
            Default width for new fields only. Existing fields keep their 1–12 width.
          </p>
        </div>
        <VisibleWhenEditor
          value={section.visibleWhen}
          fieldOptions={visibilityOptions.length ? visibilityOptions : sectionFieldOptions}
          onChange={(visibleWhen) => onChangeSection({ ...section, visibleWhen })}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border border-dashed px-4 py-10 text-center',
        colors.border,
        'bg-slate-50/50 dark:bg-slate-900/20',
      )}
    >
      <span
        className={cn(
          'mb-3 flex h-10 w-10 items-center justify-center rounded-full',
          'bg-slate-100 dark:bg-slate-800',
          colors.textMuted,
        )}
        aria-hidden
      >
        <ViewColumnsIcon className="h-5 w-5" />
      </span>
      <p className={`text-sm font-medium leading-snug ${colors.text}`}>
        Select a field or section on the canvas.
      </p>
      <p className={`mt-1.5 max-w-[14rem] text-xs leading-relaxed ${colors.textMuted}`}>
        Its properties will show up in this tab.
      </p>
    </div>
  );
}

function RedoIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return <UndoIcon className={cn(className, 'scale-x-[-1]')} />;
}

function FieldDragPreview({ field }: { field: EFormFieldSchema }) {
  const { colors } = useTheme();
  return (
    <div
      className={cn(
        'w-64 rounded-xl border px-3 py-2.5 shadow-xl ring-2 ring-blue-500/40',
        colors.border,
        colors.surface,
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', FIELD_TYPE_BADGE[field.type])}>
          <FieldTypeIcon type={field.type} className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">
            {isEFormDisplayField(field) ? eformDisplayPreviewText(field) : field.label || 'Untitled'}
          </div>
          <div className={cn('text-[11px] capitalize', colors.textMuted)}>{field.type}</div>
        </div>
      </div>
    </div>
  );
}

function RevisionsIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.05 11a9 9 0 1 1 .5 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DiffFieldChrome({
  status,
  changedProps,
  children,
}: {
  status?: 'removed' | 'changed' | 'unchanged' | null;
  changedProps?: string[];
  children: React.ReactNode;
}) {
  if (!status || status === 'unchanged') return <>{children}</>;
  const isRemoved = status === 'removed';
  return (
    <div
      className={cn(
        'relative rounded-lg p-2 -m-2 ring-2',
        isRemoved
          ? 'ring-rose-400/80 bg-rose-50/70 dark:bg-rose-950/30 dark:ring-rose-500/60'
          : 'ring-amber-400/80 bg-amber-50/70 dark:bg-amber-950/25 dark:ring-amber-500/60',
      )}
      title={
        isRemoved
          ? 'Removed from current draft'
          : changedProps?.length
            ? `Changed in draft: ${changedProps.join(', ')}`
            : 'Changed in current draft'
      }
    >
      <span
        className={cn(
          'absolute -top-2 left-2 z-[1] rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide shadow-sm',
          isRemoved
            ? 'bg-rose-600 text-white'
            : 'bg-amber-600 text-white',
        )}
      >
        {isRemoved ? 'Removed' : 'Changed'}
      </span>
      {children}
    </div>
  );
}

function RevisionDiffSummary({ diff }: { diff: EFormSchemaDiff }) {
  const { colors } = useTheme();
  const [showDetails, setShowDetails] = useState(false);

  if (diff.totalChanges === 0) {
    return (
      <div
        className={cn(
          'mb-4 rounded-xl border px-4 py-3 text-sm',
          colors.border,
          'bg-emerald-50/80 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200',
        )}
      >
        Identical to the current draft — no field or form differences.
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mb-4 rounded-xl border px-4 py-3 space-y-3',
        colors.border,
        'bg-slate-50/90 dark:bg-slate-900/40',
      )}
    >
      <div className="flex flex-wrap items-center gap-2 gap-y-1.5">
        <span className={cn('text-xs font-medium', colors.textMuted)}>vs current draft</span>
        {diff.added.length > 0 ? (
          <span className="inline-flex items-center rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">
            +{diff.added.length} added
          </span>
        ) : null}
        {diff.removed.length > 0 ? (
          <span className="inline-flex items-center rounded-md bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800 dark:bg-rose-900/50 dark:text-rose-200">
            −{diff.removed.length} removed
          </span>
        ) : null}
        {diff.changed.length > 0 ? (
          <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-900/50 dark:text-amber-200">
            ~{diff.changed.length} changed
          </span>
        ) : null}
        {diff.formMetaChanged ? (
          <span className="inline-flex items-center rounded-md bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-900 dark:bg-sky-900/50 dark:text-sky-200">
            Form settings
          </span>
        ) : null}
        <button
          type="button"
          className={cn(
            'ml-auto text-[11px] font-medium underline-offset-2 hover:underline',
            colors.textMuted,
          )}
          onClick={() => setShowDetails((v) => !v)}
        >
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
      </div>
      <p className={cn('text-[11px] leading-relaxed', colors.textMuted)}>
        Highlights on the form mark fields in this revision that were removed or changed in the
        draft. Fields added only in the draft are listed below (they are not on this revision).
      </p>
      {showDetails ? (
        <div className={cn('space-y-3 border-t pt-3 text-xs', colors.border)}>
          {diff.added.length > 0 ? (
            <div>
              <p className="font-semibold text-emerald-800 dark:text-emerald-200 mb-1">
                Added in draft
              </p>
              <ul className={cn('space-y-0.5', colors.textSecondary)}>
                {diff.added.map((f) => (
                  <li key={f.id}>
                    <span className="font-medium">{f.label}</span>
                    <span className={colors.textMuted}>
                      {' '}
                      · {f.type} · {f.sectionHeading}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {diff.removed.length > 0 ? (
            <div>
              <p className="font-semibold text-rose-800 dark:text-rose-200 mb-1">
                Removed from draft
              </p>
              <ul className={cn('space-y-0.5', colors.textSecondary)}>
                {diff.removed.map((f) => (
                  <li key={f.id}>
                    <span className="font-medium">{f.label}</span>
                    <span className={colors.textMuted}>
                      {' '}
                      · {f.type} · {f.sectionHeading}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {diff.changed.length > 0 ? (
            <div>
              <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">
                Changed in draft
              </p>
              <ul className={cn('space-y-0.5', colors.textSecondary)}>
                {diff.changed.map((f) => (
                  <li key={f.id}>
                    <span className="font-medium">{f.label}</span>
                    {f.changedProps?.length ? (
                      <span className={colors.textMuted}>
                        {' '}
                        · {f.changedProps.join(', ')}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {diff.formMetaChanges.length > 0 ? (
            <div>
              <p className="font-semibold text-sky-900 dark:text-sky-200 mb-1">Form / sections</p>
              <ul className={cn('space-y-0.5', colors.textSecondary)}>
                {diff.formMetaChanges.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function EFormSchemaLivePreview({
  schema: previewSchema,
  values,
  onChange,
  fieldDiffByKey,
  changedPropsByKey,
}: {
  schema: EFormSchema;
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  fieldDiffByKey?: Record<string, 'removed' | 'changed' | 'unchanged'>;
  changedPropsByKey?: Record<string, string[]>;
}) {
  const { colors } = useTheme();

  const wrapField = (fieldKey: string, node: React.ReactNode) => {
    const status = fieldDiffByKey?.[fieldKey];
    return (
      <DiffFieldChrome
        status={status}
        changedProps={changedPropsByKey?.[fieldKey]}
      >
        {node}
      </DiffFieldChrome>
    );
  };

  return (
    <>
      {previewSchema.instructions ? (
        <div
          className={cn(
            'mb-4 rounded-xl border px-4 py-3 text-sm whitespace-pre-wrap',
            colors.border,
            'bg-slate-50/80 dark:bg-slate-900/40',
            colors.textSecondary,
          )}
        >
          {previewSchema.instructions}
        </div>
      ) : null}
      <div className={cn('rounded-xl border p-4 sm:p-6', colors.border, colors.surface)}>
        <EFormRuntime
          schema={previewSchema}
          values={values}
          onChange={(name, value) => onChange({ ...values, [name]: value })}
          wrapField={wrapField}
          variant="default"
        />
      </div>
    </>
  );
}

function emptyPreviewValuesForSchema(previewSchema: EFormSchema): Record<string, unknown> {
  const compiled = compileFormSchema(previewSchema);
  const fieldsByKey = new Map(
    flattenSchemaFields(previewSchema).map((f) => [f.key, f] as const),
  );
  const v: Record<string, unknown> = {};
  for (const f of compiled.fields) {
    const schemaField = fieldsByKey.get(f.name);
    v[f.name] =
      schemaField?.type === 'file'
        ? schemaField.multiple
          ? []
          : null
        : schemaField?.type === 'photo' || schemaField?.type === 'signature'
          ? null
          : schemaField?.type === 'subform'
            ? []
            : '';
  }
  return v;
}

const BuilderPage: React.FC<{ studioEmbedded?: boolean }> = ({ studioEmbedded = false }) => {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? parseInt(idParam, 10) : NaN;
  const { data, isLoading, mutate } = useEFormDefinition(Number.isInteger(id) ? id : 0);
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { setTitleOverride, setSubtitle } = useTopbar();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const { schema, setSchema, setSchemaQuiet, commitHistoryFrom, replaceSchema, undo, redo, canUndo, canRedo } =
    useSchemaHistory(emptyEFormSchema());
  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([]);
  const selectedFieldId = selectedFieldIds[selectedFieldIds.length - 1] ?? null;
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [activePaletteId, setActivePaletteId] = useState<string | null>(null);
  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(null);
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<Record<string, boolean>>({});
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'unsaved' | 'saving' | 'error'>('saved');
  const dragSchemaSnapshot = React.useRef<EFormSchema | null>(null);
  const lastSavedFingerprint = React.useRef<string>('');
  const autosaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Bumped to ignore in-flight autosaves after import/replace. */
  const saveEpochRef = React.useRef(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [viewingRevision, setViewingRevision] = useState<EFormVersion | null>(null);
  const [revisionPreviewValues, setRevisionPreviewValues] = useState<Record<string, unknown>>({});
  const [importOpen, setImportOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const jsonImportInputRef = React.useRef<HTMLInputElement>(null);
  const [sourceDownloading, setSourceDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [mobileLeftOpen, setMobileLeftOpen] = useState(false);
  const [mobileRightOpen, setMobileRightOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const seeded = React.useRef(false);

  const leftPanel = useResizablePanel({
    storageKey: 'eforms-builder-left',
    defaultWidth: 240,
    minWidth: 180,
    maxWidth: 360,
    side: 'left',
  });
  const rightPanel = useResizablePanel({
    storageKey: 'eforms-builder-right',
    defaultWidth: 300,
    minWidth: 240,
    maxWidth: 480,
    side: 'right',
  });

  const { data: versionsFromApi } = useSWR<EFormVersion[]>(
    versionsOpen && Number.isInteger(id) ? `/e-form-definitions/${id}/versions` : null,
    fetcher,
  );
  const versions = versionsFromApi ?? data?.versions ?? [];

  useEffect(() => {
    setTitleOverride(name.trim() || 'Untitled form');
    setSubtitle('Form Builder');
    return () => {
      setTitleOverride(undefined);
      setSubtitle(undefined);
    };
  }, [name, setTitleOverride, setSubtitle]);

  useEffect(() => {
    if (!data || seeded.current) return;
    const nextName = data.name;
    const nextCode = data.code;
    const nextDescription = data.description ?? '';
    const nextActive = data.isActive !== false;
    setName(nextName);
    setCode(nextCode);
    setDescription(nextDescription);
    setIsActive(nextActive);
    const draft = getDraftVersion(data);
    let nextSchema = emptyEFormSchema();
    if (draft?.schemaJson && isEFormSchema(draft.schemaJson)) {
      const loaded = draft.schemaJson;
      const empty = emptyEFormSchema();
      nextSchema = {
        ...empty,
        ...loaded,
        instructions: loaded.instructions ?? empty.instructions ?? '',
        settings: {
          requireApproval: loaded.settings?.requireApproval ?? false,
          approvalTemplateCode: loaded.settings?.approvalTemplateCode,
          listColumns: loaded.settings?.listColumns,
          filterFields: loaded.settings?.filterFields,
          sidebarPlacements: loaded.settings?.sidebarPlacements,
        },
        sections: loaded.sections,
      };
      setSelectedSectionId(loaded.sections[0]?.id ?? null);
    } else {
      setSelectedSectionId(nextSchema.sections[0]?.id ?? null);
    }
    replaceSchema(nextSchema, { recordHistory: false });
    lastSavedFingerprint.current = JSON.stringify({
      name: nextName,
      code: nextCode,
      description: nextDescription,
      isActive: nextActive,
      schema: nextSchema,
    });
    setSaveStatus('saved');
    seeded.current = true;
  }, [data, replaceSchema]);

  useEffect(() => {
    if (!previewOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isEditableKeyboardTarget(e.target)) return;
      e.preventDefault();
      setPreviewOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewOpen]);

  useEffect(() => {
    if (!versionsOpen && !viewingRevision) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isEditableKeyboardTarget(e.target)) return;
      e.preventDefault();
      if (viewingRevision) {
        setViewingRevision(null);
        return;
      }
      setVersionsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [versionsOpen, viewingRevision]);

  useEffect(() => {
    if (!viewingRevision?.schemaJson || !isEFormSchema(viewingRevision.schemaJson)) {
      setRevisionPreviewValues({});
      return;
    }
    setRevisionPreviewValues(emptyPreviewValuesForSchema(viewingRevision.schemaJson));
  }, [viewingRevision]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (previewOpen || versionsOpen || importOpen || viewingRevision) return;
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;
      if (isEditableKeyboardTarget(e.target)) return;
      e.preventDefault();
      if (isUndo) undo();
      else redo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewOpen, versionsOpen, importOpen, viewingRevision, undo, redo]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (previewOpen || versionsOpen || importOpen || viewingRevision) return;
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (isEditableKeyboardTarget(e.target)) return;
      if (!selectedFieldIds.length) return;
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return;
      }
      e.preventDefault();
      const delta = e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 1;
      setSchema((prev) => {
        const next = nudgeFieldsInSchema(prev, selectedFieldIds, delta);
        return next ?? prev;
      });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [previewOpen, versionsOpen, importOpen, viewingRevision, selectedFieldIds, setSchema]);

  const draftFingerprint = useMemo(
    () => JSON.stringify({ name, code, description, isActive, schema }),
    [name, code, description, isActive, schema],
  );

  useEffect(() => {
    if (!seeded.current || !Number.isInteger(id)) return;
    if (activeFieldId || activePaletteId) return;
    if (draftFingerprint === lastSavedFingerprint.current) {
      setSaveStatus((s) => (s === 'saving' ? s : 'saved'));
      return;
    }
    setSaveStatus('unsaved');
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const epoch = saveEpochRef.current;
      void (async () => {
        setSaveStatus('saving');
        try {
          await updateEFormDefinition(id, { name, code, description, isActive });
          await saveEFormDraftSchema(id, schema);
          if (epoch !== saveEpochRef.current) return;
          lastSavedFingerprint.current = draftFingerprint;
          setSaveStatus('saved');
          void mutate();
        } catch {
          if (epoch !== saveEpochRef.current) return;
          setSaveStatus('error');
        }
      })();
    }, 2000);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [
    draftFingerprint,
    activeFieldId,
    activePaletteId,
    id,
    name,
    code,
    description,
    isActive,
    schema,
    mutate,
  ]);

  const isDirty = saveStatus !== 'saved';
  const { confirmLeave } = useUnsavedChangesGuard(isDirty);
  const leaveToCatalog = useCallback(async () => {
    if (!(await confirmLeave())) return;
    navigate('/e-forms/builder');
  }, [confirmLeave, navigate]);

  const revisionDiff = useMemo(() => {
    if (!viewingRevision || !isEFormSchema(viewingRevision.schemaJson)) return null;
    return diffEFormSchemas(viewingRevision.schemaJson, schema);
  }, [viewingRevision, schema]);

  const revisionChangedPropsByKey = useMemo(() => {
    if (!revisionDiff) return undefined;
    const map: Record<string, string[]> = {};
    for (const entry of revisionDiff.changed) {
      if (entry.changedProps?.length) map[entry.key] = entry.changedProps;
    }
    return map;
  }, [revisionDiff]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const activeType = args.active.data.current?.type as string | undefined;

    if (activeType === 'section-sort') {
      const collisions = pointerWithin(args);
      const list = collisions.length > 0 ? collisions : closestCenter(args);
      const sectionHits = list.filter((c) => {
        const data = args.droppableContainers.find((d) => d.id === c.id)?.data.current;
        return data?.type === 'section-sort' || String(c.id).startsWith('section-drop-');
      });
      return sectionHits.length > 0 ? sectionHits : closestCenter(args);
    }

    if (activeType === 'field' || activeType === 'palette') {
      const pointerHits = pointerWithin(args);
      const base = pointerHits.length > 0 ? pointerHits : closestCorners(args);

      const fieldHits = base.filter((c) => {
        const data = args.droppableContainers.find((d) => d.id === c.id)?.data.current;
        return data?.type === 'field';
      });
      if (fieldHits.length > 0) {
        return fieldHits;
      }

      const sectionHits = base.filter((c) => {
        const data = args.droppableContainers.find((d) => d.id === c.id)?.data.current;
        return data?.type === 'section' || String(c.id).startsWith('section-drop-');
      });
      if (sectionHits.length > 0) return sectionHits;

      return closestCorners(args);
    }

    return closestCenter(args);
  }, []);

  const updateField = useCallback((next: EFormFieldSchema) => {
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) => ({
        ...sec,
        fields: sec.fields.map((f) => (f.id === next.id ? next : f)),
      })),
    }));
  }, [setSchema]);

  const updateSection = useCallback((next: EFormSectionSchema) => {
    setSchema((prev) => ({
      ...prev,
      sections: prev.sections.map((sec) => (sec.id === next.id ? next : sec)),
    }));
  }, [setSchema]);

  const handleDragStart = (event: DragStartEvent) => {
    const type = event.active.data.current?.type;
    dragSchemaSnapshot.current = null;
    setDropIndicator(null);
    if (type === 'palette') {
      setActivePaletteId(String(event.active.data.current?.paletteId ?? ''));
      setActiveFieldId(null);
      return;
    }
    if (type === 'field') {
      const fid = String(event.active.id);
      setActiveFieldId(fid);
      setActivePaletteId(null);
      dragSchemaSnapshot.current = JSON.parse(JSON.stringify(schema)) as EFormSchema;
      if (!selectedFieldIds.includes(fid)) {
        setSelectedFieldIds([fid]);
      }
      return;
    }
    setActivePaletteId(null);
    setActiveFieldId(null);
  };

  const updateDropIndicator = (event: DragOverEvent | DragMoveEvent) => {
    const { active, over } = event;
    const activeType = active.data.current?.type;
    if (activeType !== 'field' && activeType !== 'palette') {
      setDropIndicator(null);
      return;
    }
    if (!over) {
      setDropIndicator(null);
      return;
    }
    const overType = over.data.current?.type;
    if (overType === 'field' && active.id !== over.id) {
      const translated = active.rect.current.translated;
      const activeMidY =
        (translated?.top ?? over.rect.top) + (translated?.height ?? over.rect.height) / 2;
      const overMidY = over.rect.top + over.rect.height / 2;
      const edge = activeMidY < overMidY ? 'before' : 'after';
      setDropIndicator({ kind: 'field', overId: String(over.id), edge });
      return;
    }
    if (overType === 'section' || String(over.id).startsWith('section-drop-')) {
      const sectionId =
        (over.data.current?.sectionId as string | undefined) ||
        String(over.id).replace('section-drop-', '');
      setDropIndicator({ kind: 'section', sectionId });
      return;
    }
    setDropIndicator(null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    updateDropIndicator(event);
  };

  const handleDragMove = (event: DragMoveEvent) => {
    updateDropIndicator(event);
  };

  const handleDragCancel = () => {
    dragSchemaSnapshot.current = null;
    setDropIndicator(null);
    setActivePaletteId(null);
    setActiveFieldId(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeType = active.data.current?.type;
    const snapshot = dragSchemaSnapshot.current;
    const indicator = dropIndicator;
    dragSchemaSnapshot.current = null;
    setDropIndicator(null);
    setActivePaletteId(null);
    setActiveFieldId(null);

    if (activeType === 'field') {
      if (indicator?.kind === 'field') {
        setSchemaQuiet((prev) => {
          const next = moveFieldInSchema(
            prev,
            String(active.id),
            indicator.overId,
            indicator.edge,
          );
          return next ?? prev;
        });
      } else if (indicator?.kind === 'section') {
        setSchemaQuiet((prev) => {
          const next = moveFieldInSchema(
            prev,
            String(active.id),
            `section-drop-${indicator.sectionId}`,
            'after',
          );
          return next ?? prev;
        });
      } else if (over && active.id !== over.id) {
        setSchemaQuiet((prev) => {
          const next = moveFieldInSchema(prev, String(active.id), String(over.id), 'before');
          return next ?? prev;
        });
      }
      if (snapshot) {
        queueMicrotask(() => commitHistoryFrom(snapshot));
      }
      return;
    }

    if (!over) return;

    const overType = over.data.current?.type;

    if (activeType === 'section-sort') {
      const oldIndex = schema.sections.findIndex((s) => s.id === active.id);
      let newIndex = schema.sections.findIndex((s) => s.id === over.id);
      if (newIndex < 0 && String(over.id).startsWith('section-drop-')) {
        const sid = String(over.id).replace('section-drop-', '');
        newIndex = schema.sections.findIndex((s) => s.id === sid);
      }
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      setSchema((prev) => ({
        ...prev,
        sections: arrayMove(prev.sections, oldIndex, newIndex),
      }));
      return;
    }

    if (activeType === 'palette') {
      const paletteId = String(active.data.current?.paletteId ?? '');
      const item = FIELD_PALETTE.find((p) => p.id === paletteId);
      const fieldType = (item?.type ?? active.data.current?.fieldType) as EFormFieldType;
      const newField = createPaletteField(fieldType, {
        display: item?.display,
        label: item?.display ? item.label : undefined,
      });
      let targetSectionId: string | null = null;
      let insertIndex = -1;

      if (indicator?.kind === 'field') {
        for (const sec of schema.sections) {
          const idx = sec.fields.findIndex((f) => f.id === indicator.overId);
          if (idx >= 0) {
            targetSectionId = sec.id;
            insertIndex = indicator.edge === 'before' ? idx : idx + 1;
            break;
          }
        }
      } else if (indicator?.kind === 'section') {
        targetSectionId = indicator.sectionId;
      } else if (overType === 'section') {
        targetSectionId = over.data.current?.sectionId as string;
      } else if (String(over.id).startsWith('section-drop-')) {
        targetSectionId = String(over.id).replace('section-drop-', '');
      } else if (overType === 'field') {
        for (const sec of schema.sections) {
          const idx = sec.fields.findIndex((f) => f.id === over.id);
          if (idx >= 0) {
            targetSectionId = sec.id;
            insertIndex = idx;
            break;
          }
        }
      }
      if (!targetSectionId) targetSectionId = schema.sections[0]?.id ?? null;
      if (!targetSectionId) return;
      const targetSection = schema.sections.find((s) => s.id === targetSectionId);
      if (targetSection && newField.colSpan !== 12) {
        newField.colSpan = defaultColSpanForSection(
          normalizeSectionColumns(targetSection.columns),
        );
      }
      setSchema((prev) => ({
        ...prev,
        sections: prev.sections.map((sec) => {
          if (sec.id !== targetSectionId) return sec;
          const fields = [...sec.fields];
          if (insertIndex >= 0) fields.splice(insertIndex, 0, newField);
          else fields.push(newField);
          return { ...sec, fields };
        }),
      }));
      setSelectedFieldIds([newField.id]);
      setSelectedSectionId(targetSectionId);
    }
  };

  const handleSave = async () => {
    if (!Number.isInteger(id)) return;
    setSaving(true);
    setSaveStatus('saving');
    try {
      await updateEFormDefinition(id, { name, code, description, isActive });
      await saveEFormDraftSchema(id, schema);
      lastSavedFingerprint.current = JSON.stringify({ name, code, description, isActive, schema });
      await mutate();
      setSaveStatus('saved');
      showSuccess('Draft saved');
    } catch (e: any) {
      setSaveStatus('error');
      showError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!Number.isInteger(id)) return;
    setPublishing(true);
    try {
      const issues = validateSchemaForPublish(schema);
      if (issues.length) {
        showError(issues.map((i) => i.message).join('; '));
        return;
      }
      await updateEFormDefinition(id, { name, code, description, isActive });
      await saveEFormDraftSchema(id, schema);
      await publishEFormDefinition(id);
      seeded.current = false;
      await mutate();
      showSuccess('Form published');
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!Number.isInteger(id)) return;
    setUnpublishing(true);
    try {
      await unpublishEFormDefinition(id);
      seeded.current = false;
      await mutate();
      showSuccess('Form unpublished');
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Unpublish failed');
    } finally {
      setUnpublishing(false);
    }
  };

  useFormStudioActions(
    studioEmbedded
      ? {
      save: handleSave,
      preview: () => setPreviewOpen(true),
      publish: handlePublish,
      saving,
      publishing,
      dirty: isDirty,
      statusLabel:
        saveStatus === 'saving'
          ? 'Saving draft…'
          : saveStatus === 'error'
            ? 'Save failed'
            : isDirty
              ? 'Unpublished changes'
              : 'Draft saved',
        }
      : null,
  );

  const handleRestoreVersion = async (versionId: number) => {
    if (!Number.isInteger(id)) return;
    setRestoringId(versionId);
    try {
      await restoreEFormVersion(id, versionId);
      seeded.current = false;
      await mutate();
      showSuccess('Revision restored to draft');
      setViewingRevision(null);
      setVersionsOpen(false);
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Restore failed');
    } finally {
      setRestoringId(null);
    }
  };

  const applyImportedJson = useCallback(
    async (file: File) => {
      let parsed: ReturnType<typeof parseEFormImportJson>;
      try {
        const text = await file.text();
        parsed = parseEFormImportJson(JSON.parse(text));
      } catch (e: any) {
        showError(e?.message || 'Invalid JSON form file.');
        return;
      }

      const ok = await confirm({
        title: 'Import JSON',
        message:
          'Replace the current draft with this JSON form? Unsaved changes will be overwritten.',
        confirmText: 'Replace draft',
        cancelText: 'Cancel',
        confirmVariant: 'danger',
      });
      if (!ok) return;

      replaceSchema(parsed.schema);
      if (parsed.name != null) setName(parsed.name);
      if (parsed.code != null) setCode(parsed.code);
      if (parsed.description != null) setDescription(parsed.description);
      if (parsed.isActive != null) setIsActive(parsed.isActive);
      setSelectedFieldIds([]);
      setSelectedSectionId(parsed.schema.sections[0]?.id ?? null);
      showSuccess('Form imported from JSON');
    },
    [confirm, replaceSchema, showError, showSuccess],
  );

  const handleExportJson = useCallback(() => {
    const pack = buildEFormPackage({
      name,
      code,
      description,
      isActive,
      schema,
    });
    const basename = safeDownloadBasename(name, code);
    downloadJsonFile(`${basename}.eform.json`, pack);
    showSuccess('Form exported as JSON');
  }, [name, code, description, isActive, schema, showSuccess]);

  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!previewOpen) return;
    setPreviewValues(emptyPreviewValuesForSchema(schema));
  }, [previewOpen, schema]);

  const hasPublished = Boolean(getPublishedVersion(data));
  const busy = saving || publishing || unpublishing || restoringId != null;

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner size="large" message="Loading builder…" />
      </div>
    );
  }

  const addSection = () => {
    const sec: EFormSectionSchema = {
      id: newSectionId(),
      heading: `Section ${schema.sections.length + 1}`,
      columns: 2,
      fields: [],
    };
    setSchema((prev) => ({ ...prev, sections: [...prev.sections, sec] }));
    setSelectedSectionId(sec.id);
    setSelectedFieldIds([]);
  };

  return (
    <div className={`h-full min-h-0 flex flex-col overflow-hidden ${colors.text}`}>
      <header
        className={cn(
          'flex-shrink-0 border-b',
          colors.border,
          colors.surface,
        )}
      >
        <div className="flex items-center gap-3 px-3 py-2 min-h-[3rem]">
          {/* Identity */}
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <div className="hidden sm:flex items-center gap-1.5 shrink-0">
              <Link
                to="/e-forms/builder"
                onClick={(e) => {
                  if (!isDirty) return;
                  e.preventDefault();
                  void leaveToCatalog();
                }}
                className={cn(
                  'text-xs font-medium hover:underline',
                  colors.textMuted,
                )}
              >
                Forms
              </Link>
              <ChevronRightIcon className={cn('w-3.5 h-3.5', colors.textMuted)} />
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className="min-w-0 flex-1 max-w-md">
                <TextInput
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  size="sm"
                  className="!min-h-8"
                  placeholder="Form name"
                  aria-label="Form name"
                />
              </div>
              <div className="w-[9.5rem] shrink-0">
                <TextInput
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  size="sm"
                  className="font-mono text-xs !min-h-8"
                  placeholder="CODE"
                  aria-label="Form code"
                />
              </div>
            </div>
            <div className="hidden md:flex items-center gap-1.5 shrink-0">
              {hasPublished ? (
                <Badge variant="success">Published</Badge>
              ) : (
                <Badge variant="warning">Draft</Badge>
              )}
              {isActive ? (
                <Badge variant="success">Enabled</Badge>
              ) : (
                <Badge variant="secondary">Disabled</Badge>
              )}
              <Badge
                variant={
                  saveStatus === 'saved'
                    ? 'success'
                    : saveStatus === 'unsaved'
                      ? 'warning'
                      : saveStatus === 'error'
                        ? 'danger'
                        : 'secondary'
                }
                title={
                  saveStatus === 'saved'
                    ? 'All changes saved'
                    : saveStatus === 'unsaved'
                      ? 'Unsaved changes — autosaves shortly'
                      : saveStatus === 'saving'
                        ? 'Saving…'
                        : 'Save failed'
                }
              >
                {saveStatus === 'saved'
                  ? 'Saved'
                  : saveStatus === 'unsaved'
                    ? 'Unsaved'
                    : saveStatus === 'saving'
                      ? 'Saving…'
                      : 'Error'}
              </Badge>
            </div>
          </div>

          {/* Actions */}
          <div className="flex shrink-0 items-center gap-2">
            <div
              className={cn(
                'inline-flex items-center rounded-lg border p-0.5',
                colors.border,
              )}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={undo}
                disabled={!canUndo || busy}
                className="!h-7 !w-7 !px-0"
                title="Undo (Ctrl+Z)"
                aria-label="Undo"
              >
                <UndoIcon className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={redo}
                disabled={!canRedo || busy}
                className="!h-7 !w-7 !px-0"
                title="Redo (Ctrl+Y)"
                aria-label="Redo"
              >
                <RedoIcon className="w-3.5 h-3.5" />
              </Button>
            </div>

            <div className={cn('hidden sm:block w-px h-5', colors.border, 'bg-current opacity-20')} />

            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPreviewOpen(true)}
              className="!h-8 gap-1.5"
              title="Preview"
            >
              <EyeIcon className="w-3.5 h-3.5" />
              <span className="hidden lg:inline">Preview</span>
            </Button>

            {!studioEmbedded ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/e-forms/builder/${id}?section=access`)}
                className="!h-8 gap-1.5"
                title="Access and workflow"
              >
                <CogIcon className="w-3.5 h-3.5" />
                <span className="hidden xl:inline">Configure</span>
              </Button>
            ) : null}

            <div className="relative">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setActionsOpen((o) => !o)}
                disabled={busy}
                className="!h-8 gap-1.5"
                title="Actions"
                aria-haspopup="menu"
                aria-expanded={actionsOpen}
              >
                <span>Actions</span>
                <ChevronDownIcon
                  className={cn('w-3.5 h-3.5 transition-transform', actionsOpen && 'rotate-180')}
                />
              </Button>
              {actionsOpen ? (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    aria-hidden
                    onClick={() => setActionsOpen(false)}
                  />
                  <div
                    role="menu"
                    className={cn(
                      'absolute right-0 top-full z-40 mt-1.5 w-56 overflow-hidden rounded-xl border py-1 shadow-lg',
                      colors.border,
                      colors.surface,
                    )}
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        'hover:bg-slate-50 dark:hover:bg-slate-800/80',
                        colors.text,
                      )}
                      disabled={busy}
                      onClick={() => {
                        setActionsOpen(false);
                        setImportOpen(true);
                      }}
                    >
                      <DocumentTextIcon className="h-3.5 w-3.5 shrink-0" />
                      Import from file
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        'hover:bg-slate-50 dark:hover:bg-slate-800/80',
                        colors.text,
                      )}
                      disabled={busy}
                      onClick={() => {
                        setActionsOpen(false);
                        jsonImportInputRef.current?.click();
                      }}
                    >
                      <UploadCloudIcon className="h-3.5 w-3.5 shrink-0" />
                      Import JSON
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        'hover:bg-slate-50 dark:hover:bg-slate-800/80',
                        colors.text,
                      )}
                      onClick={() => {
                        setActionsOpen(false);
                        handleExportJson();
                      }}
                    >
                      <DownloadIcon className="h-3.5 w-3.5 shrink-0" />
                      Export JSON
                    </button>
                    <div className={cn('my-1 border-t', colors.border)} />
                    <button
                      type="button"
                      role="menuitem"
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        'hover:bg-slate-50 dark:hover:bg-slate-800/80',
                        colors.text,
                      )}
                      onClick={() => {
                        setActionsOpen(false);
                        setVersionsOpen(true);
                      }}
                    >
                      <RevisionsIcon className="h-3.5 w-3.5 shrink-0" />
                      Revisions
                    </button>
                    <div className={cn('my-1 border-t', colors.border)} />
                    <button
                      type="button"
                      role="menuitem"
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        'hover:bg-slate-50 dark:hover:bg-slate-800/80',
                        colors.text,
                      )}
                      disabled={busy}
                      onClick={() => {
                        setActionsOpen(false);
                        void handleSave();
                      }}
                    >
                      <SaveIcon className="h-3.5 w-3.5 shrink-0" />
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                        'hover:bg-slate-50 dark:hover:bg-slate-800/80',
                        colors.text,
                      )}
                      disabled={busy}
                      onClick={() => {
                        setActionsOpen(false);
                        void handlePublish();
                      }}
                    >
                      <LockIcon className="h-3.5 w-3.5 shrink-0" />
                      {publishing ? 'Publishing…' : 'Publish'}
                    </button>
                    {hasPublished ? (
                      <button
                        type="button"
                        role="menuitem"
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                          'hover:bg-slate-50 dark:hover:bg-slate-800/80',
                          colors.text,
                        )}
                        disabled={busy}
                        onClick={() => {
                          setActionsOpen(false);
                          void handleUnpublish();
                        }}
                      >
                        <UnlockIcon className="h-3.5 w-3.5 shrink-0" />
                        {unpublishing ? 'Unpublishing…' : 'Unpublish'}
                      </button>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>

            <input
              ref={jsonImportInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void applyImportedJson(file);
              }}
            />

            <div className={cn('hidden md:block w-px h-5', colors.border, 'bg-current opacity-20')} />

            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void leaveToCatalog()}
                className="!h-8 !px-2"
                title="Back to catalog"
                aria-label="Cancel"
              >
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>

        <div
          className={cn(
            'flex flex-wrap items-center gap-x-3 gap-y-1 px-3 pb-2',
            data?.sourceFileName ? 'md:pb-1.5' : 'md:hidden',
          )}
        >
            <div className="flex md:hidden items-center gap-1.5">
              {hasPublished ? (
                <Badge variant="success">Published</Badge>
              ) : (
                <Badge variant="warning">Draft</Badge>
              )}
              {isActive ? (
                <Badge variant="success">Enabled</Badge>
              ) : (
                <Badge variant="secondary">Disabled</Badge>
              )}
              <Badge
                variant={
                  saveStatus === 'saved'
                    ? 'success'
                    : saveStatus === 'unsaved'
                      ? 'warning'
                      : saveStatus === 'error'
                        ? 'danger'
                        : 'secondary'
                }
              >
                {saveStatus === 'saved'
                  ? 'Saved'
                  : saveStatus === 'unsaved'
                    ? 'Unsaved'
                    : saveStatus === 'saving'
                      ? 'Saving…'
                      : 'Error'}
              </Badge>
            </div>
            {data?.sourceFileName ? (
              <div className={cn('flex min-w-0 items-center gap-2 text-[11px]', colors.textMuted)}>
                <DocumentTextIcon className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate max-w-[14rem] sm:max-w-[20rem]" title={data.sourceFileName}>
                  {data.sourceFileName}
                </span>
                {data.sourceFileSize ? <span>{formatFileSize(data.sourceFileSize)}</span> : null}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-teal-700 dark:text-teal-400 hover:underline disabled:opacity-50"
                  disabled={sourceDownloading || !Number.isInteger(id)}
                  onClick={async () => {
                    if (!Number.isInteger(id)) return;
                    setSourceDownloading(true);
                    try {
                      await downloadEFormSourceFile(id);
                    } catch {
                      // Global API interceptor already toasts.
                    } finally {
                      setSourceDownloading(false);
                    }
                  }}
                >
                  <DownloadIcon className="w-3 h-3" />
                  {sourceDownloading ? '…' : 'Download'}
                </button>
              </div>
            ) : null}
            <div className="flex sm:hidden items-center gap-1 ml-auto">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setImportOpen(true)}
                disabled={busy}
                className="!h-7 !px-2"
                title="Import"
              >
                <DocumentTextIcon className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPreviewOpen(true)}
                className="!h-7 !px-2"
                title="Preview"
              >
                <EyeIcon className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVersionsOpen(true)}
                className="!h-7 !px-2"
                title="Revisions"
              >
                <RevisionsIcon className="w-3.5 h-3.5" />
              </Button>
            </div>
        </div>
      </header>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragMove={handleDragMove}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        <div
          ref={(node) => {
            (leftPanel.splitRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            (rightPanel.splitRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }}
          className={cn(
            'flex-1 min-h-0 flex',
            (leftPanel.panelResizing || rightPanel.panelResizing) && 'select-none',
          )}
        >
          {!leftPanel.collapsed ? (
            <aside
              style={{ width: leftPanel.panelWidth }}
              className={cn(
                'hidden lg:flex shrink-0 flex-col border-r overflow-hidden',
                colors.border,
                colors.surface,
              )}
            >
              <div
                className={cn(
                  'flex items-start justify-between gap-2 px-3 pt-3 pb-1 shrink-0',
                )}
              >
                <PanelHeading title="Palette" hint="Drag elements onto the canvas" />
                <ReferenceSidebarCollapseButton
                  collapsed={false}
                  onToggle={() => leftPanel.setCollapsed(true)}
                  side="left"
                  className="mt-0.5"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
                <BuilderPaletteContent
                  query={paletteQuery}
                  onQueryChange={setPaletteQuery}
                  onAddSection={addSection}
                />
              </div>
            </aside>
          ) : (
            <div
              className={cn(
                'hidden lg:flex w-10 shrink-0 flex-col items-center border-r py-3 gap-2',
                colors.border,
                colors.surface,
              )}
            >
              <ReferenceSidebarCollapseButton
                collapsed
                onToggle={() => leftPanel.setCollapsed(false)}
                side="left"
              />
              <span
                className={cn(
                  'text-[10px] font-semibold uppercase tracking-wide [writing-mode:vertical-rl] rotate-180',
                  colors.textMuted,
                )}
              >
                Palette
              </span>
            </div>
          )}

          {!leftPanel.collapsed ? (
            <div className="hidden lg:flex">
              <ReferenceSidebarSplitter
                panelWidth={leftPanel.panelWidth}
                minWidth={leftPanel.minWidth}
                maxWidth={leftPanel.maxWidth}
                resizing={leftPanel.panelResizing}
                onMouseDown={leftPanel.startResize}
                label="Resize palette panel"
              />
            </div>
          ) : null}

          <main
            className={cn(
              'min-w-0 flex-1 overflow-y-auto p-4 md:p-5 relative',
              'bg-slate-50/70 dark:bg-slate-950/40',
            )}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold tracking-tight">Canvas</p>
                <p className={`text-xs ${colors.textMuted}`}>
                  {schema.sections.length} section
                  {schema.sections.length === 1 ? '' : 's'} ·{' '}
                  {flattenSchemaFields(schema).length} field
                  {flattenSchemaFields(schema).length === 1 ? '' : 's'}
                  {selectedFieldIds.length > 1
                    ? ` · ${selectedFieldIds.length} selected`
                    : null}
                  <span className="hidden xl:inline">
                    {' '}
                    · Shift-click multi-select · Alt+↑/↓ reorder
                  </span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {selectedFieldIds.length > 1 ? (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="!h-8 gap-1.5"
                      onClick={() => {
                        const fid = selectedFieldIds[0];
                        const sec = schema.sections.find((s) =>
                          s.fields.some((f) => f.id === fid),
                        );
                        if (!sec) return;
                        // reuse section handler via synthetic click path
                        const ids = selectedFieldIds;
                        const copies: string[] = [];
                        setSchema((prev) => {
                          let next = prev;
                          for (const id of ids) {
                            const s0 = next.sections.find((s) => s.fields.some((f) => f.id === id));
                            const original = s0?.fields.find((f) => f.id === id);
                            if (!s0 || !original) continue;
                            const copy = duplicateField(original);
                            copies.push(copy.id);
                            next = {
                              ...next,
                              sections: next.sections.map((s) => {
                                if (s.id !== s0.id) return s;
                                const idx = s.fields.findIndex((f) => f.id === id);
                                if (idx < 0) return s;
                                const fields = [...s.fields];
                                fields.splice(idx + 1, 0, copy);
                                return { ...s, fields };
                              }),
                            };
                          }
                          return next;
                        });
                        if (copies.length) setSelectedFieldIds(copies);
                      }}
                    >
                      <CopyIcon className="w-3.5 h-3.5" />
                      Duplicate
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="!h-8 gap-1.5 text-red-600"
                      onClick={() => {
                        const ids = selectedFieldIds;
                        setSchema((prev) => ({
                          ...prev,
                          sections: prev.sections.map((sec) => ({
                            ...sec,
                            fields: sec.fields.filter((f) => !ids.includes(f.id)),
                          })),
                        }));
                        setSelectedFieldIds([]);
                      }}
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                      Delete
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="!h-8"
                      onClick={() => setSelectedFieldIds([])}
                    >
                      Clear
                    </Button>
                    <div className={cn('w-px h-5 mx-0.5', colors.border, 'bg-current opacity-20')} />
                  </>
                ) : null}
                <div
                  className={cn(
                    'inline-flex items-center rounded-lg border p-0.5',
                    colors.border,
                  )}
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="!h-7 !w-7 !px-0"
                    title="Zoom out"
                    disabled={canvasZoom <= CANVAS_ZOOM_STEPS[0]}
                    onClick={() => setCanvasZoom((z) => nextZoom(z, -1))}
                  >
                    <span className="text-sm font-semibold leading-none">−</span>
                  </Button>
                  <button
                    type="button"
                    className={cn('px-2 text-[11px] tabular-nums min-w-[3rem]', colors.textMuted)}
                    title="Reset zoom"
                    onClick={() => setCanvasZoom(1)}
                  >
                    {Math.round(canvasZoom * 100)}%
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="!h-7 !w-7 !px-0"
                    title="Zoom in"
                    disabled={canvasZoom >= CANVAS_ZOOM_STEPS[CANVAS_ZOOM_STEPS.length - 1]}
                    onClick={() => setCanvasZoom((z) => nextZoom(z, 1))}
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="!h-8 !w-8 !px-0"
                  title="Fit width"
                  aria-label="Fit width"
                  onClick={() => setCanvasZoom(0.9)}
                >
                  <FitWidthIcon className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="!h-8 !w-8 !px-0"
                  title="Collapse all sections"
                  aria-label="Collapse all sections"
                  onClick={() => {
                    const next: Record<string, boolean> = {};
                    for (const s of schema.sections) next[s.id] = true;
                    setCollapsedSectionIds(next);
                  }}
                >
                  <CollapseAllIcon className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="!h-8 !w-8 !px-0"
                  title="Expand all sections"
                  aria-label="Expand all sections"
                  onClick={() => setCollapsedSectionIds({})}
                >
                  <ExpandAllIcon className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-1 lg:hidden">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setMobileLeftOpen((o) => !o)}
                  >
                    {mobileLeftOpen ? 'Hide palette' : 'Palette'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setMobileRightOpen((o) => !o)}
                  >
                    {mobileRightOpen ? 'Hide properties' : 'Properties'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Mobile / tablet: stack panels when expanded via buttons */}
            <div className="lg:hidden mb-4 space-y-3">
              {mobileLeftOpen ? (
                <div className={cn('rounded-xl border p-3', colors.border, colors.surface)}>
                  <div className="flex items-center justify-between mb-2">
                    <PanelHeading title="Palette" />
                    <ReferenceSidebarCollapseButton
                      collapsed={false}
                      onToggle={() => setMobileLeftOpen(false)}
                      side="left"
                    />
                  </div>
                  <BuilderPaletteContent
                    query={paletteQuery}
                    onQueryChange={setPaletteQuery}
                    onAddSection={addSection}
                  />
                </div>
              ) : null}
            </div>

            <div
              className="origin-top-left transition-transform duration-150"
              style={{
                transform: canvasZoom === 1 ? undefined : `scale(${canvasZoom})`,
                width: canvasZoom === 1 ? undefined : `${100 / canvasZoom}%`,
              }}
            >
            <SortableContext
              items={schema.sections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {schema.sections.map((section) => (
                <SortableSection
                  key={section.id}
                  section={section}
                  selectedFieldId={selectedFieldId}
                  selectedFieldIds={selectedFieldIds}
                  selectedSectionId={selectedSectionId}
                  dropIndicator={dropIndicator}
                  collapsed={Boolean(collapsedSectionIds[section.id])}
                  onToggleCollapse={() =>
                    setCollapsedSectionIds((prev) => ({
                      ...prev,
                      [section.id]: !prev[section.id],
                    }))
                  }
                  onSelectSection={() => {
                    setSelectedSectionId(section.id);
                    setSelectedFieldIds([]);
                  }}
                  onSelectField={(fid, e) => {
                    setSelectedSectionId(section.id);
                    if (e.shiftKey && selectedFieldIds.length > 0) {
                      const anchor = selectedFieldIds[selectedFieldIds.length - 1];
                      setSelectedFieldIds(
                        rangeSelectFieldIds(schema, section.id, anchor, fid),
                      );
                      return;
                    }
                    if (e.metaKey || e.ctrlKey) {
                      setSelectedFieldIds((prev) =>
                        prev.includes(fid)
                          ? prev.filter((id) => id !== fid)
                          : [...prev, fid],
                      );
                      return;
                    }
                    setSelectedFieldIds([fid]);
                  }}
                  onDuplicateField={(fid) => {
                    const ids =
                      selectedFieldIds.includes(fid) && selectedFieldIds.length > 1
                        ? selectedFieldIds
                        : [fid];
                    const copies: string[] = [];
                    setSchema((prev) => {
                      let next = prev;
                      for (const id of ids) {
                        const sec = next.sections.find((s) => s.fields.some((f) => f.id === id));
                        const original = sec?.fields.find((f) => f.id === id);
                        if (!sec || !original) continue;
                        const copy = duplicateField(original);
                        copies.push(copy.id);
                        next = {
                          ...next,
                          sections: next.sections.map((s) => {
                            if (s.id !== sec.id) return s;
                            const idx = s.fields.findIndex((f) => f.id === id);
                            if (idx < 0) return s;
                            const fields = [...s.fields];
                            fields.splice(idx + 1, 0, copy);
                            return { ...s, fields };
                          }),
                        };
                      }
                      return next;
                    });
                    if (copies.length) {
                      setSelectedFieldIds(copies);
                      setSelectedSectionId(section.id);
                    }
                  }}
                  onDeleteField={(fid) => {
                    const ids =
                      selectedFieldIds.includes(fid) && selectedFieldIds.length > 1
                        ? selectedFieldIds
                        : [fid];
                    setSchema((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) => ({
                        ...sec,
                        fields: sec.fields.filter((f) => !ids.includes(f.id)),
                      })),
                    }));
                    setSelectedFieldIds((prev) => prev.filter((id) => !ids.includes(id)));
                  }}
                  onDuplicateSection={() => {
                    const copy = duplicateSection(section);
                    setSchema((prev) => {
                      const idx = prev.sections.findIndex((s) => s.id === section.id);
                      const sections = [...prev.sections];
                      sections.splice(idx + 1, 0, copy);
                      return { ...prev, sections };
                    });
                    setSelectedSectionId(copy.id);
                    setSelectedFieldIds([]);
                  }}
                  onDeleteSection={() => {
                    if (schema.sections.length <= 1) {
                      showError('Form must have at least one section');
                      return;
                    }
                    setSchema((prev) => ({
                      ...prev,
                      sections: prev.sections.filter((s) => s.id !== section.id),
                    }));
                    if (selectedSectionId === section.id) setSelectedSectionId(null);
                    setSelectedFieldIds((ids) =>
                      ids.filter(
                        (fid) => !section.fields.some((f) => f.id === fid),
                      ),
                    );
                  }}
                />
              ))}
            </SortableContext>
            </div>

            <div className="lg:hidden mt-4 space-y-3">
              {mobileRightOpen ? (
                <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.surface)}>
                  <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-1">
                    <PanelHeading title="Inspector" hint="Form, list, and selection" />
                    <ReferenceSidebarCollapseButton
                      collapsed={false}
                      onToggle={() => setMobileRightOpen(false)}
                      side="right"
                    />
                  </div>
                  <div className="max-h-[70vh] min-h-[20rem] flex flex-col">
                    <InspectorPanel
                      schema={schema}
                      onChangeSchema={setSchema}
                      formName={name}
                      isActive={isActive}
                      onIsActiveChange={setIsActive}
                      description={description}
                      onDescriptionChange={setDescription}
                      selectedFieldId={selectedFieldId}
                      selectedSectionId={selectedSectionId}
                      onChangeField={updateField}
                      onChangeSection={updateSection}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </main>

          {!rightPanel.collapsed ? (
            <div className="hidden lg:flex">
              <ReferenceSidebarSplitter
                panelWidth={rightPanel.panelWidth}
                minWidth={rightPanel.minWidth}
                maxWidth={rightPanel.maxWidth}
                resizing={rightPanel.panelResizing}
                onMouseDown={rightPanel.startResize}
                label="Resize properties panel"
              />
            </div>
          ) : null}

          {!rightPanel.collapsed ? (
            <aside
              style={{ width: rightPanel.panelWidth }}
              className={cn(
                'hidden lg:flex shrink-0 flex-col border-l overflow-hidden',
                colors.border,
                colors.surface,
              )}
            >
              <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-1 shrink-0">
                <PanelHeading title="Inspector" hint="Form, list, and selection settings" />
                <ReferenceSidebarCollapseButton
                  collapsed={false}
                  onToggle={() => rightPanel.setCollapsed(true)}
                  side="right"
                  className="mt-0.5"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-hidden flex flex-col">
                <InspectorPanel
                  schema={schema}
                  onChangeSchema={setSchema}
                  formName={name}
                  isActive={isActive}
                  onIsActiveChange={setIsActive}
                  description={description}
                  onDescriptionChange={setDescription}
                  selectedFieldId={selectedFieldId}
                  selectedSectionId={selectedSectionId}
                  onChangeField={updateField}
                  onChangeSection={updateSection}
                />
              </div>
            </aside>
          ) : (
            <div
              className={cn(
                'hidden lg:flex w-10 shrink-0 flex-col items-center border-l py-3 gap-2',
                colors.border,
                colors.surface,
              )}
            >
              <ReferenceSidebarCollapseButton
                collapsed
                onToggle={() => rightPanel.setCollapsed(false)}
                side="right"
              />
              <span
                className={cn(
                  'text-[10px] font-semibold uppercase tracking-wide [writing-mode:vertical-rl]',
                  colors.textMuted,
                )}
              >
                Inspector
              </span>
            </div>
          )}
        </div>

        <DragOverlay dropAnimation={{ duration: 180, easing: 'ease' }}>
          {activeFieldId ? (
            (() => {
              const field = flattenSchemaFields(schema).find((f) => f.id === activeFieldId);
              return field ? <FieldDragPreview field={field} /> : null;
            })()
          ) : activePaletteId ? (
            <div
              className={cn(
                'flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-lg',
                colors.border,
                colors.surface,
              )}
            >
              {(() => {
                const item = FIELD_PALETTE.find((p) => p.id === activePaletteId);
                if (!item) return null;
                return (
                  <>
                    <span
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-lg',
                        FIELD_TYPE_BADGE[item.type],
                      )}
                    >
                      {item.display ? (
                        <LockIcon className="w-3.5 h-3.5" />
                      ) : (
                        <FieldTypeIcon type={item.type} className="w-3.5 h-3.5" />
                      )}
                    </span>
                    {item.label}
                  </>
                );
              })()}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {previewOpen ? (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-[1px]"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className={cn(
              'flex h-full w-full max-w-4xl flex-col shadow-2xl border-l',
              colors.border,
              colors.surface,
            )}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="eform-preview-title"
          >
            <header
              className={cn(
                'flex shrink-0 items-center gap-3 border-b px-4 py-3',
                colors.border,
                'bg-slate-50/90 dark:bg-slate-900/50',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                  'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
                )}
              >
                <EyeIcon className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 id="eform-preview-title" className="text-sm font-semibold tracking-tight">
                    Preview
                  </h2>
                  <Badge variant="warning">Draft</Badge>
                </div>
                <p className={cn('truncate text-xs mt-0.5', colors.textMuted)} title={name || undefined}>
                  {name?.trim() || 'Untitled form'}
                  {code?.trim() ? (
                    <span className="font-mono"> · {code.trim()}</span>
                  ) : null}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="!h-8 !w-8 !px-0 shrink-0"
                title="Close preview"
                aria-label="Close preview"
                onClick={() => setPreviewOpen(false)}
              >
                <XIcon className="w-4 h-4" />
              </Button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              <p className={cn('text-xs mb-4', colors.textMuted)}>
                Interactive draft — changes here are not saved. Publish when ready.
              </p>
              <EFormSchemaLivePreview
                schema={schema}
                values={previewValues}
                onChange={setPreviewValues}
              />
            </div>
          </div>
        </div>
      ) : null}

      {versionsOpen ? (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-[1px]"
          onClick={() => {
            if (viewingRevision) return;
            setVersionsOpen(false);
          }}
        >
          <div
            className={cn(
              'flex h-full w-full max-w-md flex-col shadow-2xl border-l',
              colors.border,
              colors.surface,
            )}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="eform-revisions-title"
          >
            <header
              className={cn(
                'flex shrink-0 items-center gap-3 border-b px-4 py-3',
                colors.border,
                'bg-slate-50/90 dark:bg-slate-900/50',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                  'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
                )}
              >
                <RevisionsIcon className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 id="eform-revisions-title" className="text-sm font-semibold tracking-tight">
                  Revisions
                </h2>
                <p className={cn('truncate text-xs mt-0.5', colors.textMuted)}>
                  View or restore a previous revision
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="!h-8 !w-8 !px-0 shrink-0"
                title="Close revisions"
                aria-label="Close revisions"
                onClick={() => {
                  setViewingRevision(null);
                  setVersionsOpen(false);
                }}
              >
                <XIcon className="w-4 h-4" />
              </Button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {versions.length === 0 ? (
                <div
                  className={cn(
                    'rounded-xl border border-dashed px-4 py-10 text-center',
                    colors.border,
                  )}
                >
                  <p className={`text-sm font-medium ${colors.text}`}>No revisions yet</p>
                  <p className={`text-xs mt-1 ${colors.textMuted}`}>
                    Publish the form to create the first revision history entry.
                  </p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {[...versions]
                    .sort((a, b) => b.version - a.version)
                    .map((v) => {
                      const fieldCount = isEFormSchema(v.schemaJson)
                        ? flattenSchemaFields(v.schemaJson).length
                        : 0;
                      const sectionCount = isEFormSchema(v.schemaJson)
                        ? v.schemaJson.sections.length
                        : 0;
                      return (
                        <li
                          key={v.id}
                          className={cn(
                            'rounded-xl border p-3 transition-colors',
                            colors.border,
                            colors.surface,
                            viewingRevision?.id === v.id &&
                              'ring-2 ring-violet-400/70 border-violet-300 dark:border-violet-700',
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium flex flex-wrap items-center gap-2">
                                <span className="font-mono">r{v.version}</span>
                                <Badge
                                  variant={
                                    v.status === 'PUBLISHED'
                                      ? 'success'
                                      : v.status === 'DRAFT'
                                        ? 'warning'
                                        : 'secondary'
                                  }
                                >
                                  {v.status}
                                </Badge>
                              </p>
                              <p className={`text-xs mt-1 ${colors.textMuted}`}>
                                {v.publishedAt
                                  ? `Published ${new Date(v.publishedAt).toLocaleString()}`
                                  : `Updated ${new Date(v.updatedAt).toLocaleString()}`}
                              </p>
                              <p className={`text-[11px] mt-1 ${colors.textMuted}`}>
                                {sectionCount} section{sectionCount === 1 ? '' : 's'} · {fieldCount}{' '}
                                field{fieldCount === 1 ? '' : 's'}
                              </p>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-1.5">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="!h-8 gap-1.5"
                              disabled={!isEFormSchema(v.schemaJson)}
                              onClick={() => setViewingRevision(v)}
                              title="View this revision"
                            >
                              <EyeIcon className="w-3.5 h-3.5" />
                              View
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="!h-8 gap-1.5"
                              disabled={busy}
                              onClick={() => handleRestoreVersion(v.id)}
                              title="Restore to draft"
                            >
                              <UndoIcon className="w-3.5 h-3.5" />
                              {restoringId === v.id ? 'Restoring…' : 'Restore'}
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {viewingRevision && isEFormSchema(viewingRevision.schemaJson) ? (
        <div
          className="fixed inset-0 z-[60] flex justify-end bg-black/40 backdrop-blur-[1px]"
          onClick={() => setViewingRevision(null)}
        >
          <div
            className={cn(
              'flex h-full w-full max-w-4xl flex-col shadow-2xl border-l',
              colors.border,
              colors.surface,
            )}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="eform-revision-view-title"
          >
            <header
              className={cn(
                'flex shrink-0 items-center gap-3 border-b px-4 py-3',
                colors.border,
                'bg-slate-50/90 dark:bg-slate-900/50',
              )}
            >
              <span
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                  'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
                )}
              >
                <EyeIcon className="w-4 h-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2
                    id="eform-revision-view-title"
                    className="text-sm font-semibold tracking-tight font-mono"
                  >
                    Revision r{viewingRevision.version}
                  </h2>
                  <Badge
                    variant={
                      viewingRevision.status === 'PUBLISHED'
                        ? 'success'
                        : viewingRevision.status === 'DRAFT'
                          ? 'warning'
                          : 'secondary'
                    }
                  >
                    {viewingRevision.status}
                  </Badge>
                </div>
                <p className={cn('truncate text-xs mt-0.5', colors.textMuted)}>
                  Read-only view · {name?.trim() || 'Untitled form'}
                </p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="!h-8 gap-1.5 shrink-0"
                disabled={busy}
                onClick={() => handleRestoreVersion(viewingRevision.id)}
                title="Restore to draft"
              >
                <UndoIcon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">
                  {restoringId === viewingRevision.id ? 'Restoring…' : 'Restore'}
                </span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="!h-8 !w-8 !px-0 shrink-0"
                title="Close revision view"
                aria-label="Close revision view"
                onClick={() => setViewingRevision(null)}
              >
                <XIcon className="w-4 h-4" />
              </Button>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              <p className={cn('text-xs mb-4', colors.textMuted)}>
                Exploring this revision does not change your current draft until you restore it.
              </p>
              {revisionDiff ? <RevisionDiffSummary diff={revisionDiff} /> : null}
              <EFormSchemaLivePreview
                schema={viewingRevision.schemaJson}
                values={revisionPreviewValues}
                onChange={setRevisionPreviewValues}
                fieldDiffByKey={revisionDiff?.revisionFieldStatusByKey}
                changedPropsByKey={revisionChangedPropsByKey}
              />
            </div>
          </div>
        </div>
      ) : null}

      <ImportFromFileModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        definitionId={id}
        onImported={(result) => {
          setImportOpen(false);
          const def = result.definition;
          const nextName = def.name;
          const nextCode = def.code;
          const nextDescription = def.description ?? '';
          const nextActive = def.isActive !== false;
          const draft = getDraftVersion(def as EFormDefinition);
          const rawSchema =
            (result.schema && isEFormSchema(result.schema) ? result.schema : null) ??
            (draft?.schemaJson && isEFormSchema(draft.schemaJson) ? draft.schemaJson : null);

          if (!rawSchema) {
            showError('Import finished but no form fields were returned. Try again or use a clearer PDF/Word file.');
            void mutate();
            return;
          }

          const empty = emptyEFormSchema();
          const nextSchema: EFormSchema = {
            ...empty,
            ...rawSchema,
            instructions: rawSchema.instructions ?? empty.instructions ?? '',
            settings: {
              requireApproval: rawSchema.settings?.requireApproval ?? false,
              approvalTemplateCode: rawSchema.settings?.approvalTemplateCode,
              listColumns: rawSchema.settings?.listColumns,
              filterFields: rawSchema.settings?.filterFields,
              sidebarPlacements: rawSchema.settings?.sidebarPlacements,
            },
            sections: rawSchema.sections,
          };

          // Cancel any in-flight autosave that could overwrite the imported draft.
          saveEpochRef.current += 1;
          if (autosaveTimer.current) {
            clearTimeout(autosaveTimer.current);
            autosaveTimer.current = null;
          }

          setName(nextName);
          setCode(nextCode);
          setDescription(nextDescription);
          setIsActive(nextActive);
          replaceSchema(nextSchema, { recordHistory: true });
          setSelectedSectionId(nextSchema.sections[0]?.id ?? null);
          setSelectedFieldIds([]);
          lastSavedFingerprint.current = JSON.stringify({
            name: nextName,
            code: nextCode,
            description: nextDescription,
            isActive: nextActive,
            schema: nextSchema,
          });
          setSaveStatus('saved');
          seeded.current = true;
          void mutate();

          const fieldCount = flattenSchemaFields(nextSchema).length;
          const extra = result.warnings?.length ? ` ${result.warnings[0]}` : '';
          showSuccess(
            `Draft replaced from file · ${fieldCount} field${fieldCount === 1 ? '' : 's'}.${extra}`,
          );
        }}
      />
    </div>
  );
};

const PALETTE_CATEGORIES: Array<{
  key: (typeof FIELD_PALETTE)[number]['category'];
  label: string;
}> = [
  { key: 'layout', label: 'Layout' },
  { key: 'text', label: 'Text elements' },
  { key: 'date', label: 'Date elements' },
  { key: 'other', label: 'Other elements' },
];

function matchesPaletteSearch(
  item: (typeof FIELD_PALETTE)[number],
  query: string,
  categoryLabel: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    item.label,
    item.type,
    item.id.replace(/_/g, ' '),
    item.category,
    categoryLabel,
    item.display ? 'display static layout' : '',
  ]
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((token) => haystack.includes(token));
}

function BuilderPaletteContent({
  query,
  onQueryChange,
  onAddSection,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  onAddSection: () => void;
}) {
  const { colors } = useTheme();
  const filteredCategories = useMemo(() => {
    return PALETTE_CATEGORIES.map((cat) => ({
      ...cat,
      items: FIELD_PALETTE.filter(
        (p) => p.category === cat.key && matchesPaletteSearch(p, query, cat.label),
      ),
    })).filter((cat) => cat.items.length > 0);
  }, [query]);

  return (
    <>
      <div className="mb-3">
        <TextInput
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search elements…"
          size="sm"
          inputMode="search"
          autoComplete="off"
          showClearButton
          aria-label="Search palette elements"
          prefix={<SearchIcon className="h-3.5 w-3.5" />}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full mb-4 justify-center"
        onClick={onAddSection}
      >
        <PlusIcon className="w-4 h-4 mr-1.5" />
        Add section
      </Button>
      {filteredCategories.length === 0 ? (
        <p className={cn('px-1 py-4 text-center text-xs', colors.textMuted)}>
          No elements match “{query.trim()}”
        </p>
      ) : (
        filteredCategories.map((cat) => (
          <div key={cat.key} className="mb-4">
            <p
              className={`text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${colors.textMuted}`}
            >
              {cat.label}
            </p>
            <div className="space-y-1.5">
              {cat.items.map((p) => (
                <PaletteItem key={p.id} item={p} />
              ))}
            </div>
          </div>
        ))
      )}
    </>
  );
}

function PaletteItem({ item }: { item: (typeof FIELD_PALETTE)[number] }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${item.id}`,
    data: { type: 'palette', fieldType: item.type, paletteId: item.id, display: item.display },
  });
  const { colors } = useTheme();
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn(
        'group flex items-center gap-2 rounded-xl border px-2 py-2 text-xs cursor-grab transition-colors',
        colors.border,
        colors.surface,
        'hover:bg-slate-50 dark:hover:bg-slate-800/80',
        isDragging && 'opacity-50 ring-2 ring-blue-400/50',
      )}
    >
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
          FIELD_TYPE_BADGE[item.type],
        )}
      >
        {item.display ? (
          <LockIcon className="w-3.5 h-3.5" />
        ) : (
          <FieldTypeIcon type={item.type} className="w-3.5 h-3.5" />
        )}
      </span>
      <span className="min-w-0 flex-1 font-medium truncate">{item.label}</span>
      <GripVerticalIcon className={cn('w-3.5 h-3.5 shrink-0 opacity-40 group-hover:opacity-70', colors.textMuted)} />
    </div>
  );
}

export default BuilderPage;
